import { useState, useEffect } from "react";
import { BarChart2, TrendingUp, TrendingDown, AlertTriangle, Flame, Gamepad2, Calendar } from "lucide-react";
import { C, GRADES, TIERS, fmt, fmtS, fmtP, fmtN, sign, FMP_BASE, FMP_KEY, ANALYZER_SYS, NEWS_SYS, HAIKU, SONNET } from './constants';
import fetchFMP from './utils/fetchFMP';
import { callClaude } from './utils/callClaude';
import { normalizeClaudeData, lastMarketClose } from './utils/helpers';

// ── Components ────────────────────────────────────────────────────────────────
import GradeCircle from './components/GradeCircle';
import ConfidenceBadge from './components/ConfidenceBadge';
import ConfidenceBanner from './components/ConfidenceBanner';
import ModelCard from './components/ModelCard';
import CatalystCard from './components/CatalystCard';
import PeerAnalysis from './components/PeerAnalysis';
import IndustryContext from './components/IndustryContext';
import LoadingGame from './components/LoadingGame';
import PageHeader from './components/PageHeader';
import BottomNav from './components/BottomNav';
import SignupGateModal from './components/SignupGateModal';
import LoginModal from './components/LoginModal';
import TickerBadge from './components/TickerBadge';

// ── Pages ─────────────────────────────────────────────────────────────────────
import ContactPage from './pages/ContactPage';
import SubscribePage from './pages/SubscribePage';
import MarketCommentaryPage from './pages/MarketCommentaryPage';
import MispricedPage from './pages/MispricedPage';
import InterestRatesPage from './pages/InterestRatesPage';
import EarningsPage from './pages/EarningsPage';
import AboutPage from './pages/AboutPage';
import PerformancePage from './pages/PerformancePage';
import AdminPage from './pages/AdminPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import AccessibilityPage from './pages/AccessibilityPage';
import WatchlistPage from './pages/WatchlistPage';
import PortfolioPage from './pages/PortfolioPage';
import SharePage from './pages/SharePage';
import OptionsPage from './pages/OptionsPage';
import NotFoundPage from './pages/NotFoundPage';

export default function App() {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path === "/track-record") { window.location.replace("/performance"); return null; }

  let Page;
  if      (path === "/contact")              Page = <ContactPage />;
  else if (path === "/subscribe")            Page = <SubscribePage />;
  else if (path === "/market-commentary")    Page = <MarketCommentaryPage />;
  else if (path === "/mispriced-stocks")     Page = <MispricedPage />;
  else if (path === "/rates")             Page = <InterestRatesPage />;
  else if (path === "/earnings")          Page = <EarningsPage />;
  else if (path === "/about-us")          Page = <AboutPage />;
  else if (path === "/performance")       Page = <PerformancePage />;
  else if (path === "/admin")             Page = <AdminPage />;
  else if (path === "/privacy-policy")    Page = <PrivacyPage />;
  else if (path === "/terms")             Page = <TermsPage />;
  else if (path === "/accessibility")     Page = <AccessibilityPage />;
  else if (path === "/watchlist")         Page = <WatchlistPage />;
  else if (path === "/compare")           Page = <InterestRatesPage />;
  else if (path === "/portfolio")         Page = <PortfolioPage />;
  else if (path === "/options")           Page = <OptionsPage />;
  else if (path.startsWith("/share/"))    Page = <SharePage />;
  else {
    const known = new Set(["/","/contact","/subscribe","/market-commentary","/mispriced-stocks","/rates","/earnings","/about-us","/performance","/admin","/privacy-policy","/terms","/accessibility","/watchlist","/compare","/portfolio","/options"]);
    Page = known.has(path) ? <MainApp /> : <NotFoundPage />;
  }

  return (
    <>
      <style>{`.hma-bottom-nav{display:none}@media(max-width:768px){.hma-bottom-nav{display:flex!important;flex-direction:row!important}}`}</style>
      {Page}
      <BottomNav />
    </>
  );
}

function MainApp() {
  const [ticker,        setTicker]        = useState("");
  const [loading,       setLoading]       = useState(false);
  const [stage,         setStage]         = useState("");
  const [data,          setData]          = useState(null);
  const [err,           setErr]           = useState("");
  const [recentTickers, setRecentTickers] = useState(()=>{ try{ return JSON.parse(localStorage.getItem("hma_recent")||"[]"); }catch(e){return [];} });
  const [earningsDate,  setEarningsDate]  = useState(null);
  const [followUpQ,     setFollowUpQ]     = useState("");
  const [copied,        setCopied]        = useState(false);
  const [shareOpen,     setShareOpen]     = useState(false);
  const [votd,          setVotd]          = useState(null);
  const [votdLoading,   setVotdLoading]   = useState(true);
  const [followUpA,     setFollowUpA]     = useState(null);
  const [earningsPreview, setEarningsPreview] = useState([]);
  const [radarTickers,   setRadarTickers]   = useState([]);
  const [headlines,      setHeadlines]      = useState([]);

  const [followUpLoading,setFollowUpLoading] = useState(false);
  const [session,       setSession]       = useState(()=>{ try{ return localStorage.getItem("hma_session")||null; }catch{ return null; } });
  const [loginOpen,     setLoginOpen]     = useState(false);
  const [gateOpen,      setGateOpen]      = useState(false);
  const [pointsToast,   setPointsToast]   = useState(null); // { pts, reason }
  const [watchlistAdded,setWatchlistAdded]= useState(false);
  const [watchlistLoading,setWatchlistLoading] = useState(false);
  const [shareId,       setShareId]       = useState(null);
  const [shareLoading,  setShareLoading]  = useState(false);

  // Sync admin flags from session
  useEffect(()=>{
    if (!session) return;
    fetch(`/api/user?session=${session}`).then(r=>r.ok?r.json():null).then(u=>{
      if(u?.email) {
        if (u.isAdmin) { try { localStorage.setItem("hma_pro","1"); localStorage.setItem("hma_admin","1"); } catch {} }
      } else {
        setSession(null); try{localStorage.removeItem("hma_session");}catch{}
      }
    }).catch(()=>{});
  }, [session]);

  // Handle ?token= magic link verification
  useEffect(()=>{
    const params = new URLSearchParams(window.location.search);
    const token = params.get("token");
    if (!token) return;
    // Remove token from URL immediately
    window.history.replaceState({}, "", window.location.pathname);
    fetch(`/api/auth?token=${token}`).then(r=>r.json()).then(d=>{
      if (d.session) {
        try { localStorage.setItem("hma_session", d.session); } catch {}
        if (d.isAdmin) { try { localStorage.setItem("hma_pro","1"); localStorage.setItem("hma_admin","1"); } catch {} }
        setSession(d.session);
      }
    }).catch(()=>{});
  }, []);

  const addPoints = (pts, reason) => {
    if (!session) return;
    fetch("/api/user", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ session, pts, reason }) })
      .then(r=>r.ok?r.json():null).then(u=>{ if(u?.email){ setPointsToast({ pts, reason }); setTimeout(()=>setPointsToast(null), 2500); } }).catch(()=>{});
  };

  useEffect(()=>{
    // News (cached or fresh)
    (()=>{
      try {
        const nc = localStorage.getItem("hma_news");
        if (nc) { const {d,ts}=JSON.parse(nc); if(Date.now()-ts<1800000&&d?.stories){setHeadlines(d.stories);return;} }
      } catch(e) {}
      callClaude(NEWS_SYS, "Find 8 most important financial market news stories from today.", 1200, true, false, HAIKU)
        .then(d=>{ if(d?.stories){ setHeadlines(d.stories); try{localStorage.setItem("hma_news",JSON.stringify({d,ts:Date.now()}));}catch(e){} } })
        .catch(()=>{});
    })();
    // Auto-analyze from URL param (?t=AAPL)
    const params = new URLSearchParams(window.location.search);
    const tParam = params.get("t");
    if (tParam) {
      const sym = tParam.toUpperCase().trim();
      setTicker(sym);
      setTimeout(()=>analyze(sym), 200);
    }

    // Valuation of the Day — then use its trending list for hot stonks
    fetch("/api/votd").then(r=>r.json()).then(d=>{
      if(d.ticker) {
        setVotd(d);
        const runners = (d.trendingList||[]).filter(s=>s!==d.ticker).slice(0,8);
        if(runners.length>0) {
          // Set immediately with just symbols so cards show up right away
          setRadarTickers(runners.map(s=>({ symbol:s, name:s, changesPercentage:0, price:0 })));
          // Then enrich each with live price — one fetch per ticker in parallel
          Promise.allSettled(
            runners.map(s=>
              fetch(`${FMP_BASE}/stable/quote?symbol=${s}&apikey=${FMP_KEY}`)
                .then(r=>r.json())
                .then(arr=>Array.isArray(arr)?arr[0]:arr)
            )
          ).then(results=>{
            const enriched = results.map((r,i)=>{
              if(r.status==="fulfilled"&&r.value?.symbol) {
                const q=r.value;
                return { symbol:q.symbol, name:q.name||runners[i], changesPercentage:q.changesPercentage||0, price:q.price||0, marketCap:q.marketCap||0 };
              }
              return { symbol:runners[i], name:runners[i], changesPercentage:0, price:0, marketCap:0 };
            });
            setRadarTickers(enriched);
          });
        }
      }
    }).catch(()=>{}).finally(()=>setVotdLoading(false));

    // Earnings preview for homepage — large caps only, no weekends
    const from = new Date().toISOString().slice(0,10);
    const to   = new Date(Date.now()+7*86400000).toISOString().slice(0,10);
    Promise.all([
      fetch(`${FMP_BASE}/stable/earnings-calendar?from=${from}&to=${to}&apikey=${FMP_KEY}`).then(r=>r.json()).catch(()=>[]),
      fetch(`${FMP_BASE}/stable/company-screener?marketCapMoreThan=2000000000&limit=500&country=US&isActivelyTrading=true&apikey=${FMP_KEY}`).then(r=>r.json()).catch(()=>[]),
    ]).then(([earns, screen]) => {
      const lcSet = new Set((Array.isArray(screen)?screen:[]).map(s=>s.symbol));
      const filtered = (Array.isArray(earns)?earns:[]).filter(e=>{
        if (!e.symbol || e.symbol.includes(".") || e.symbol.includes("-")) return false;
        const dow = new Date(e.date+"T12:00:00").getDay();
        if (dow===0||dow===6) return false; // skip weekends
        return lcSet.size===0 || lcSet.has(e.symbol);
      });
      setEarningsPreview(filtered.slice(0,6));
    }).catch(()=>{});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps



  async function analyze(sym) {
    const rawInput = (sym || ticker).trim();
    if (!rawInput) return;
    // gate disabled during dev
    if (sym) setTicker(sym);
    setEarningsDate(null); setFollowUpA(null); setFollowUpQ("");

    setLoading(true); setData(null); setErr("");
    const stages = ["Connecting to market data…","Pulling live fundamentals…","Running DCF & multi-stage models…","Running P/E, EV/EBITDA, Graham…","Analyzing catalysts & peers…","Building industry context…","Finalizing your valuation report…"];
    let si = 0; setStage(stages[0]);
    const iv = setInterval(()=>{ si=Math.min(si+1,stages.length-1); setStage(stages[si]); }, 6000);

    try {
      // STEP 0: Resolve ticker (handles company names like "Apple" or "Coca-Cola")
      let t = rawInput.toUpperCase();
      const looksLikeTicker = /^[A-Z0-9.-]{1,6}$/.test(t);
      if (!looksLikeTicker) {
        setStage("Looking up company…");
        const searchRes = await fetch(`${FMP_BASE}/stable/search-name?query=${encodeURIComponent(rawInput)}&limit=10&apikey=${FMP_KEY}`);
        const results = await searchRes.json();
        const match = (Array.isArray(results) ? results : []).find(r =>
          ["NASDAQ","NYSE","AMEX"].includes(r.exchange) && !r.symbol.includes(".")
        );
        if (!match) {
          clearInterval(iv);
          setErr(`No stock found matching "${rawInput}". Check the ticker or company name and try again (e.g. "AAPL" or "Apple").`);
          setLoading(false); setStage("");
          return;
        }
        t = match.symbol;
        setTicker(t);
      }

      // 1. Check localStorage (same user, same browser)
      try {
        const cached = localStorage.getItem(`hma_${t}`);
        if (cached) {
          const { data:cd, ts } = JSON.parse(cached);
          if (ts > lastMarketClose()) { clearInterval(iv); setData(normalizeClaudeData(cd)); setLoading(false); setStage(""); return; }
        }
      } catch(e) {}

      // 2. Check server cache (any user this trading session)
      try {
        const sr = await fetch(`/api/cache?t=${t}`);
        if (sr.ok) {
          const { data:cd } = await sr.json();
          if (cd) {
            const d = normalizeClaudeData(cd);
            try { localStorage.setItem(`hma_${t}`, JSON.stringify({ data:d, ts:Date.now() })); } catch(e) {}
            clearInterval(iv); setData(d); setLoading(false); setStage(""); return;
          }
        }
      } catch(e) {}

      // STEP 1: Fetch FMP fundamentals + earnings calendar in parallel
      setStage("Fetching live fundamentals…");
      let fund = null;
      try {
        [fund] = await Promise.all([
          fetchFMP(t),
          // Earnings calendar — fire and forget, don't block analysis
          (()=>{
            const from = new Date().toISOString().slice(0,10);
            const to   = new Date(Date.now()+14*86400000).toISOString().slice(0,10);
            fetch(`${FMP_BASE}/stable/earnings-calendar?from=${from}&to=${to}&apikey=${FMP_KEY}`)
              .then(r=>r.json())
              .then(events=>{
                if (!Array.isArray(events)) return;
                const upcoming = events.find(ev => ev.symbol === t);
                if (upcoming) setEarningsDate(upcoming.date);
              })
              .catch(()=>{});
          })(),
        ]);
      } catch(fmpErr) {
        // FMP failed — try company name search as fallback
        setStage("Looking up company…");
        const searchRes = await fetch(`${FMP_BASE}/stable/search-name?query=${encodeURIComponent(rawInput)}&limit=10&apikey=${FMP_KEY}`);
        const results = await searchRes.json();
        const match = (Array.isArray(results) ? results : []).find(r =>
          ["NASDAQ","NYSE","AMEX"].includes(r.exchange) && !r.symbol.includes(".")
        );
        if (!match) {
          clearInterval(iv);
          setErr(`"${rawInput}" doesn't appear to be a valid ticker or company name. Double-check and try again.`);
          setLoading(false); setStage("");
          return;
        }
        t = match.symbol;
        setTicker(t);
        try { fund = await fetchFMP(t); } catch(e2) {}
      }

      // STEP 2: Claude runs 12 models
      setStage("Running 12 valuation models…");
      const essentials = fund ? {
        ticker:t, companyName:fund.companyName, currentPrice:fund.currentPrice,
        dataDate:fund.dataDate, sector:fund.sector,
        eps:fund.eps, bookValuePerShare:fund.bookValuePerShare,
        revenuePerShare:fund.revenuePerShare, revenueGrowthRate:fund.revenueGrowthRate,
        ebitda:fund.ebitda, freeCashFlow:fund.freeCashFlow,
        dividendPerShare:fund.dividendPerShare, payoutRatio:fund.payoutRatio,
        dividendGrowthRate:fund.dividendGrowthRate, beta:fund.beta,
        riskFreeRate:fund.riskFreeRate, wacc:fund.wacc,
        netDebt:fund.netDebt, sharesOutstanding:fund.sharesOutstanding,
        enterpriseValue:fund.enterpriseValue, peRatioTTM:fund.peRatioTTM,
      } : null;

      const userMsg = essentials
        ? `FMP fundamentals for ${t}: ${JSON.stringify(essentials)}. Run 12 models, grade, 3 catalysts with real numbers, 3-4 peers, 4 industry bullets. Return ONLY raw JSON.`
        : `Analyze ${t}. Web search for fundamentals. Run 12 models, grade, 3 catalysts, peers, industry bullets. Return ONLY raw JSON.`;

      const raw = await callClaude(ANALYZER_SYS, userMsg, 3500, !fund, false, SONNET, session);
      clearInterval(iv);

      if (fund) {
        if (!raw.currentPrice && fund.currentPrice) raw.currentPrice = fund.currentPrice;
        if (!raw.companyName  && fund.companyName)  raw.companyName  = fund.companyName;
        if (!raw.dataDate)                           raw.dataDate     = fund.dataDate;
        if (raw.eps               == null) raw.eps               = fund.eps;
        if (raw.beta              == null) raw.beta              = fund.beta;
        if (raw.wacc              == null) raw.wacc              = fund.wacc;
        if (raw.peRatioTTM        == null) raw.peRatioTTM        = fund.peRatioTTM;
        if (raw.revenueGrowthRate == null) raw.revenueGrowthRate = fund.revenueGrowthRate;
        if (raw.dividendPerShare  == null) raw.dividendPerShare  = fund.dividendPerShare;
        if (raw.dividendGrowthRate== null) raw.dividendGrowthRate= fund.dividendGrowthRate;
        if (raw.analystTarget     == null) raw.analystTarget     = fund.analystTarget;
        if (raw.analystTargetHigh == null) raw.analystTargetHigh = fund.analystTargetHigh;
        if (raw.analystTargetLow  == null) raw.analystTargetLow  = fund.analystTargetLow;
      }
      const d = normalizeClaudeData(raw);
      setRecentTickers(prev => {
        const updated = [t, ...prev.filter(x=>x!==t)].slice(0,5);
        try { localStorage.setItem("hma_recent", JSON.stringify(updated)); } catch(e) {}
        return updated;
      });
      try { localStorage.setItem(`hma_${t}`, JSON.stringify({data:d,ts:Date.now()})); } catch(e2) {}
      // Write to server cache so other users skip this Claude call
      fetch("/api/cache", { method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ ticker:t, data:d }) }).catch(()=>{});
      setData(d);
      if (navigator.vibrate) { navigator.vibrate(d.grade === 'A+' || d.grade === 'A' ? [50, 100, 200] : d.grade === 'F' ? [200] : [100]); }
      addPoints(10, "analysis");
      // gate disabled during dev
    } catch(e) {
      clearInterval(iv);
      const msg = e.message||"";
      if (msg === "limit") setErr("__limit__");
      else if (msg.includes("429")) setErr("Rate limited — please wait 60 seconds and try again.");
      else if (msg.includes("401")) setErr("API key error. Check ANTHROPIC_API_KEY in Vercel settings.");
      else setErr(`Couldn't analyze "${rawInput}". Check the ticker or company name and try again.`);
    }
    finally { setLoading(false); setStage(""); }
  }

  async function askFollowUp() {
    if (!followUpQ.trim() || !data) return;
    setFollowUpLoading(true); setFollowUpA(null);
    try {
      const ctx = `Stock: ${data.ticker} (${data.companyName}). Grade: ${data.grade}. Fair value: $${data.compositeFairValue}. Current price: $${data.currentPrice}. Upside: ${data.percentUpside}%. Models: ${(data.models||[]).map(m=>`${m.name}: $${m.fairValue}`).join(", ")}. Catalysts: ${(data.catalysts||[]).map(c=>c.title).join(", ")}.`;
      const sys = "You are a financial analyst assistant. Answer questions about a specific stock analysis in plain English. Be concise (2-4 sentences max). No disclaimers, no preamble — just answer directly.";
      const answer = await callClaude(sys, `Analysis context: ${ctx}\n\nUser question: ${followUpQ}`, 400, false, true, HAIKU);
      setFollowUpA(answer);
    } catch(e) {
      setFollowUpA("Couldn't get an answer. Please try again.");
    }
    setFollowUpLoading(false);
  }

  const g      = data ? (GRADES[data.grade]||GRADES["C"]) : null;
  const tier   = data ? (TIERS[data.tier]||TIERS.mature)  : null;
  const inc    = data ? (data.models||[]).filter(m=>m.included)  : [];
  const exc    = data ? (data.models||[]).filter(m=>!m.included) : [];
  const dollar = data?.dollarUpside??data?.upside??data?.upsideDownside??0;
  const pct    = data?.percentUpside??data?.percentageUpside??data?.pctUpside??0;
  const score  = data?.score ?? 50;
  const upside = dollar >= 0;
  const EXAMPLES = ["KO","AAPL","NVDA","MSFT","JNJ","AMZN"];

  return (
    <div style={{ fontFamily:"'Inter',sans-serif", background:C.bg, minHeight:"100vh", color:C.text }}>
      {loginOpen && <LoginModal onClose={()=>setLoginOpen(false)} onLogin={()=>setLoginOpen(false)}/>}
      {gateOpen && <SignupGateModal onClose={()=>setGateOpen(false)} onLogin={()=>{ setGateOpen(false); }} />}
      {/* Points toast */}
      {pointsToast && (
        <div style={{ position:"fixed", bottom:24, right:24, zIndex:500, background:"linear-gradient(135deg,#0D0800,#1A0F00)", border:"1px solid rgba(201,168,76,0.35)", borderRadius:14, padding:"12px 18px", boxShadow:"0 4px 24px rgba(0,0,0,0.8)", animation:"hmaScorePop .4s ease-out", display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ display:"flex" }}>{pointsToast.reason === "analysis" ? <BarChart2 size={20} strokeWidth={1.5} color="#e8c875"/> : <Gamepad2 size={20} strokeWidth={1.5} color="#60A5FA"/>}</span>
          <div>
            <div style={{ fontSize:14, fontWeight:900, color:C.gold2 }}>+{pointsToast.pts} pts</div>
            <div style={{ fontSize:10, color:C.hint }}>{pointsToast.reason === "analysis" ? "Analysis complete" : "Game points"}</div>
          </div>
        </div>
      )}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@700;900&family=Rajdhani:wght@600;700;800&family=Inter:wght@300;400;500;600&family=Syne:wght@400;600;700;800&family=Space+Mono:wght@400;700&display=swap');
        @keyframes hmapulse{0%,100%{opacity:.2;transform:scale(.75)}50%{opacity:1;transform:scale(1.1)}}
        @keyframes hmaticker{0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
        @keyframes hmaScorePop{0%{transform:scale(1)}40%{transform:scale(1.5)}100%{transform:scale(1)}}
        @keyframes hmaShake{0%,100%{transform:translateX(0)}20%{transform:translateX(-8px)}40%{transform:translateX(8px)}60%{transform:translateX(-5px)}80%{transform:translateX(5px)}}
        @keyframes hmaFlash{0%{opacity:.55}100%{opacity:0}}
        @keyframes hmaShimmer{0%{background-position:200% center}100%{background-position:-100% center}}
        .hma-logo-hail{background:linear-gradient(90deg,#5a3d0e 0%,#a07030 20%,#e8c875 35%,#FFE87A 50%,#e8c875 65%,#a07030 80%,#5a3d0e 100%);background-size:250% auto;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;animation:hmaShimmer 4s linear infinite}
        .hma-logo-mary{background:linear-gradient(180deg,#FFFFFF 0%,#D0E2F4 55%,#9BB5CC 100%);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
        html,body{background:#060606 !important;background-color:#060606 !important;color:#fff;min-height:100vh}
        #root,#app{background:#060606 !important;min-height:100vh}
        *{box-sizing:border-box} input,button,select{font-family:inherit}
        .hma-input{width:100%;padding:13px 16px;font-size:16px;border:1px solid rgba(201,168,76,0.25);border-radius:12px;background:#111;color:#e8c875;outline:none;transition:border-color .2s;letter-spacing:.06em;font-weight:700;font-family:'Rajdhani',sans-serif}
        .hma-input::placeholder{color:rgba(255,255,255,0.2);font-weight:400;font-size:14px;letter-spacing:.02em;font-family:'Inter',sans-serif}
        .hma-input:focus{border-color:rgba(201,168,76,0.5)}
        .hma-chip{padding:7px 16px;font-size:13px;font-weight:700;background:rgba(255,255,255,0.05);color:rgba(255,255,255,0.55);border:1px solid rgba(255,255,255,0.12);border-radius:20px;cursor:pointer;transition:all .15s;font-family:'Rajdhani',sans-serif;letter-spacing:.05em}
        .hma-chip:hover{background:rgba(201,168,76,0.1);color:#e8c875;border-color:rgba(201,168,76,0.3)}
        .hma-search-row{display:flex;gap:8px}
        @media(max-width:480px){.hma-search-row{flex-direction:column}.hma-chip{padding:8px 18px;font-size:14px}}
        .hma-ham-btn{display:none;align-items:center;justify-content:center;width:44px;height:44px;min-width:44px;border-radius:10px;background:rgba(255,255,255,0.05);border:1px solid rgba(201,168,76,0.15);cursor:pointer;color:#fff;font-size:22px;padding:0;line-height:1}
        .hma-bottom-nav{display:none;position:fixed;bottom:0;left:0;right:0;z-index:99;background:rgba(6,6,6,0.97);backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px);border-top:1px solid rgba(201,168,76,0.14);padding-bottom:env(safe-area-inset-bottom,0px)}
        .hma-mobile-nav{display:none;flex-direction:column;gap:0;position:fixed;top:58px;left:0;right:0;background:rgba(6,6,6,0.98);border-bottom:1px solid rgba(201,168,76,0.15);z-index:98;padding:8px 12px 14px;backdrop-filter:blur(20px);-webkit-backdrop-filter:blur(20px)}
        .hma-mobile-nav.open{display:flex}
        .hma-mobile-nav a{display:block;padding:12px 14px;font-family:'Syne',sans-serif;font-size:13px;font-weight:700;letter-spacing:.06em;text-transform:uppercase;color:rgba(255,255,255,0.55);border-radius:6px;text-decoration:none;transition:color .15s,background .15s;border-bottom:1px solid rgba(201,168,76,0.06)}
        .hma-mobile-nav a:last-child{border-bottom:none}
        .hma-mobile-nav a.hma-mob-active{color:#e8c875;background:rgba(201,168,76,0.07)}
        .hma-mobile-nav a.hma-mob-sub{background:linear-gradient(135deg,#8a5a10,#c9a84c,#e8c875);color:#000!important;font-weight:800;text-align:center;margin-top:8px;border-radius:4px;border-bottom:none}
        @media(max-width:500px){.hma-logo-hail{font-size:17px!important;letter-spacing:3px!important}.hma-logo-mary{font-size:17px!important;letter-spacing:3px!important}}
        @media(max-width:768px){
          /* ── Nav ── */
          .hma-ham-btn{display:flex!important}
          .hma-svg-line{display:none!important}
          .hma-desktop-nav{display:none!important}
          .hma-nav-links{display:none!important}
          .hma-header-inner{padding:0 16px!important}
          .hma-bottom-nav{display:flex!important}
          /* ── Page padding ── */
          .hma-main-pad{padding:14px 18px 80px!important}
          .hma-track-pad{padding:20px 18px 60px!important}
          .hma-about-pad{padding:20px 18px 60px!important}
          /* ── Hide noise on mobile ── */
          .hma-news-ticker{display:none!important}
          .hma-votd-why-today{display:none!important}
          .hma-votd-catalyst-risk{display:none!important}
          .hma-votd-header-date{display:none!important}
          .hma-recent-tickers{display:none!important}
          /* ── Hero ── */
          .hma-hero-h1{font-size:28px!important;letter-spacing:0!important}
          .hma-hero-slogan{font-size:16px!important;white-space:nowrap!important;overflow:hidden!important;text-overflow:ellipsis!important;text-align:center!important;margin-bottom:14px!important;font-weight:800!important;letter-spacing:.01em!important;line-height:1.4!important}
          .hma-slogan-br{display:none!important}
          .hma-slogan-noise{display:none!important}
          .hma-homepage-hero{padding:20px 0 16px!important}
          .hma-search-row{flex-direction:column!important;gap:10px!important}
          .hma-input{padding:16px 18px!important;font-size:16px!important;border-radius:14px!important}
          .hma-analyze-hero-btn{width:100%!important;padding:16px!important;font-size:16px!important;border-radius:14px!important;justify-content:center!important}
          .hma-chips-row{flex-wrap:nowrap!important;overflow-x:auto!important;padding-bottom:2px!important;-webkit-overflow-scrolling:touch!important}
          /* ── Sections ── */
          .hma-home-two-col{grid-template-columns:1fr!important;gap:0!important}
          /* ── Section background banding ── */
          .hma-two-col-cell{background:#080808!important;margin:0 -18px!important;padding:24px 18px!important}
          .hma-earnings-col{background:#04040F!important;margin-top:0!important;padding-top:24px!important;border-top:none!important}
          .hma-tools-section{background:#050509!important;margin:0 -18px!important;padding:22px 18px!important}
          .hma-section-hd-title{font-size:18px!important}
          .hma-section-hd-sub{font-size:12px!important;letter-spacing:.02em!important;text-transform:none!important}
          /* ── VOTD compact ── */
          .hma-votd-inner{padding:16px 14px!important}
          .hma-votd-hero{flex-direction:column!important;gap:8px!important}
          .hma-votd-ticker{font-size:26px!important;line-height:1!important;letter-spacing:0!important}
          .hma-votd-grade-col{min-width:unset!important;width:100%!important;display:flex!important;flex-direction:row!important;align-items:center!important;gap:10px!important;padding:10px 12px!important;border-radius:10px!important;text-align:left!important}
          .hma-votd-grade-label{display:none!important}
          .hma-votd-grade-letter{font-size:38px!important;margin-bottom:0!important;line-height:1!important;flex-shrink:0!important}
          .hma-votd-grade-dir{margin-bottom:0!important;font-size:10px!important}
          .hma-votd-grade-fv{border-top:none!important;padding-top:0!important;margin-bottom:0!important;text-align:left!important;flex:1!important}
          .hma-votd-card-hd{margin-bottom:10px!important}
          /* ── Hide card descriptions on mobile ── */
          .hma-card-desc{display:none!important}
          .hma-earn-narrative{display:none!important}
          /* ── Tighter card padding ── */
          .hma-stock-card{padding:10px 12px!important}
          /* ── Card carousels ── */
          .hma-two-col-cell{min-width:0;position:relative}
          .hma-two-col-cell::after{content:'';position:absolute;right:0;top:auto;bottom:14px;width:48px;height:72px;background:linear-gradient(to right,transparent,#000);pointer-events:none;z-index:2}
          .hma-card-list{flex-direction:row!important;overflow-x:auto!important;overflow-y:visible!important;gap:10px!important;padding:0 40px 8px 0!important;-webkit-overflow-scrolling:touch!important;scrollbar-width:none!important;width:100%!important;scroll-snap-type:x mandatory!important}
          .hma-stock-card{min-width:120px!important;flex-shrink:0!important;flex-direction:column!important;align-items:flex-start!important;gap:6px!important;width:120px!important;border-left-width:2px!important;scroll-snap-align:start!important}
          .hma-card-row{flex-direction:column!important;align-items:flex-start!important;gap:1px!important;margin-bottom:0!important}
          .hma-card-mc{display:none!important}
          .hma-earn-row{flex-direction:column!important;align-items:flex-start!important;gap:1px!important;margin-bottom:0!important}
          .hma-earn-name{display:none!important}
          .hma-swipe-hint{display:inline-flex!important;align-items:center!important;gap:4px!important;font-size:10px!important;color:rgba(201,168,76,0.6)!important;font-weight:700!important;letter-spacing:.06em!important;margin-left:8px!important;animation:hmaSwipeHint 1.8s ease-in-out 1.2s 3!important}
          /* ── VOTD headline + button ── */
          .hma-votd-headline{overflow:hidden!important;display:-webkit-box!important;-webkit-line-clamp:2!important;-webkit-box-orient:vertical!important;font-size:14px!important;margin-bottom:6px!important}
          .hma-votd-grade-btn{width:auto!important;padding:7px 12px!important;font-size:11px!important}
          /* ── Hide tools strip (bottom nav covers it) ── */
          .hma-tools-section{display:none!important}
          /* ── Tools 2×2 grid ── */
          .hma-feat-strip{display:grid!important;grid-template-columns:1fr 1fr!important}
          .hma-feat-strip-item{border-right:1px solid rgba(201,168,76,0.1)!important;border-bottom:1px solid rgba(201,168,76,0.1)!important;padding:18px 14px!important}
          .hma-feat-strip-item:nth-child(even){border-right:none!important}
          .hma-feat-strip-item:nth-child(3),.hma-feat-strip-item:nth-child(4){border-bottom:none!important}
          /* ── Analysis grids ── */
          .hma-search-grid{grid-template-columns:1fr!important}
          .hma-models-grid{grid-template-columns:1fr 1fr!important}
          .hma-exc-grid{grid-template-columns:1fr 1fr!important}
          .hma-cat-grid{grid-template-columns:1fr!important}
          .hma-mispriced-grid{grid-template-columns:1fr!important}
          .hma-sub-grid{grid-template-columns:1fr!important}
          .hma-feat-grid{grid-template-columns:1fr!important}
          .hma-earn-prev-grid{grid-template-columns:1fr!important}
          .hma-model-card{padding:12px 14px!important}
          /* ── Results page ── */
          .hma-bull-bear-grid{grid-template-columns:1fr!important}
          .hma-rdcf-grid{grid-template-columns:1fr!important}
          .hma-price-grid{display:none!important}
          .hma-mos-bar{display:flex!important}
          .hma-grade-hero-pad{padding:16px 14px!important}
          .hma-grade-hero-grid{grid-template-columns:auto 1fr!important;gap:14px!important;align-items:start!important}
          .hma-grade-scale-bars{display:none!important}
          .hma-hero-center{display:flex!important;flex-direction:column!important;gap:0!important}
          .hma-hero-stat-grp{margin-bottom:13px}
          .hma-hero-lbl{color:rgba(201,168,76,0.88)!important;font-size:11px!important;margin-bottom:3px!important;letter-spacing:.14em!important}
          .hma-mobile-only{display:block!important}
          .hma-mobile-price-range{display:block!important}
          .hma-results-actions-desktop{display:none!important}
          .hma-results-sticky-footer{display:flex!important;position:fixed;bottom:70px;left:0;right:0;z-index:50;background:rgba(6,6,6,0.95);backdrop-filter:blur(8px);-webkit-backdrop-filter:blur(8px);border-top:1px solid rgba(201,168,76,0.13);padding:10px 16px;gap:8px;align-items:center}
          .hma-gh-right{min-width:unset!important;flex-direction:column!important;flex-wrap:nowrap!important;gap:4px!important;grid-column:1/-1!important;padding-top:4px!important}
          .hma-upside-box{background:transparent!important;border:none!important;padding:0!important;text-align:left!important;border-radius:0!important}
          .hma-upside-label{display:none!important}
          .hma-fv-big{font-size:30px!important}
          .hma-grade-dollar-val{font-size:22px!important}
          .hma-grade-dollar-pct{font-size:13px!important}
          .hma-models-grid{grid-template-columns:1fr!important}
          .hma-model-formula{display:none!important}
          .hma-ticker-name{font-size:28px!important}
          .hma-company-name{font-size:14px!important}
          .hma-ah-ticker-row{gap:8px!important}
          .hma-grade-dollar-label{font-size:11px!important}
          .hma-grade-dollar-val{font-size:28px!important}
          .hma-grade-dollar-pct{font-size:14px!important}
          .hma-badges-row{flex-wrap:wrap!important;gap:5px!important}
          .hma-bull-bear-col{border-right:none!important;border-bottom:1px solid rgba(255,255,255,0.07)!important;padding:14px!important}
          .hma-bull-bear-col:last-child{border-bottom:none!important}
          .hma-share-btn-group{display:none!important}
          .hma-fundamentals-chip{flex:1 1 calc(50% - 4px)!important;min-width:calc(50% - 4px)!important}
          /* ── Text readability on mobile ── */
          .hma-stat-label{font-size:11px!important;color:rgba(255,255,255,0.72)!important}
          .hma-stat-val{font-size:20px!important}
          .hma-body-text{font-size:13px!important;color:rgba(255,255,255,0.9)!important;line-height:1.65!important}
          .hma-bull-bear-text{font-size:13px!important;color:rgba(255,255,255,0.88)!important;line-height:1.65!important}
          .hma-company-name{font-size:12px!important;color:rgba(255,255,255,0.72)!important}
          .hma-ticker-name{font-size:20px!important}

          /* ── Boost dim inline text across all main-app sub-sections ── */
          .hma-main-pad [style*="font-size: 10px"],
          .hma-main-pad [style*="font-size: 11px"]{ font-size:13px!important; line-height:1.55!important }
          .hma-main-pad [style*="font-size: 12px"]{ font-size:14px!important; line-height:1.6!important }
          .hma-main-pad [style*="rgba(255,255,255,0.1"],
          .hma-main-pad [style*="rgba(255,255,255,0.2"]{ color:rgba(255,255,255,0.60)!important }
          .hma-main-pad [style*="rgba(255,255,255,0.3"]{ color:rgba(255,255,255,0.75)!important }
          .hma-main-pad [style*="rgba(255,255,255,0.4"],
          .hma-main-pad [style*="rgba(255,255,255,0.5"]{ color:rgba(255,255,255,0.85)!important }
        }
        .hma-chips-row::-webkit-scrollbar{display:none}
        .hma-chips-row{scrollbar-width:none}
        .hma-card-list::-webkit-scrollbar{display:none}
        .hma-swipe-hint{display:none}
        @keyframes hmaSwipeHint{0%,100%{transform:translateX(0);opacity:.6}50%{transform:translateX(4px);opacity:1}}
        @keyframes hmaHeroGlow{0%,100%{opacity:0.7}50%{opacity:1}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(22px)}to{opacity:1;transform:translateY(0)}}
        .hma-fade-up{animation:fadeUp .55s ease-out both}
        .hma-hero-section{position:relative;text-align:center;padding:72px 24px 56px;overflow:hidden}
        .hma-hero-section::before{content:'';position:absolute;inset:0;background:radial-gradient(ellipse 80% 55% at 50% 0%,rgba(201,168,76,0.10) 0%,transparent 70%);pointer-events:none}
        .hma-hero-h1{font-family:'Syne',sans-serif;font-size:60px;font-weight:800;line-height:1.06;color:#fff;margin:0 0 14px;letter-spacing:-.01em}
        .hma-hero-h1 .gold{color:#e8c875}
        .hma-homepage-hero{position:relative;padding:28px 0 20px;overflow:hidden}
        .hma-homepage-hero::before{content:'';position:absolute;top:-60px;left:50%;transform:translateX(-50%);width:800px;height:280px;background:radial-gradient(ellipse at center,rgba(201,168,76,0.09) 0%,transparent 70%);pointer-events:none;z-index:0}
        .hma-hero-slogan{position:relative;z-index:1;font-family:'Syne',sans-serif;font-size:26px;font-weight:800;line-height:1.2;margin-bottom:5px;letter-spacing:.02em;text-align:center;white-space:nowrap}
        .hma-slogan-br{display:none}
        .hma-hero-tagline{position:relative;z-index:1;font-family:'Space Mono',monospace;font-size:10px;color:rgba(201,168,76,0.5);letter-spacing:.18em;text-transform:uppercase;font-weight:700;margin-bottom:18px;text-align:center}
        .hma-stats-strip{display:flex;gap:0;background:rgba(201,168,76,0.04);border:1px solid rgba(201,168,76,0.13);border-radius:12px;overflow:hidden;margin-bottom:20px}
        .hma-stats-strip-item{flex:1;padding:11px 10px;text-align:center;border-right:1px solid rgba(201,168,76,0.1)}
        .hma-stats-strip-item:last-child{border-right:none}
        .hma-stats-num{font-family:'Rajdhani',sans-serif;font-size:17px;font-weight:900;color:#e8c875;line-height:1}
        .hma-stats-lbl{font-family:'Space Mono',monospace;font-size:8px;font-weight:700;color:rgba(255,255,255,0.32);letter-spacing:.1em;text-transform:uppercase;margin-top:2px}
        .hma-hero-lbl{font-family:'Space Mono',monospace;font-size:9px;font-weight:700;color:rgba(201,168,76,0.52);letter-spacing:.16em;text-transform:uppercase;margin-bottom:4px}
        .hma-mobile-only{display:none}
        .hma-home-two-col{display:grid;grid-template-columns:1fr 1fr;gap:20px;align-items:start}
        .hma-section-hd{display:flex;flex-direction:column;gap:5px;padding-left:12px;border-left:2.5px solid #c9a84c}
        .hma-section-hd-blue{border-left-color:rgba(99,160,255,0.8)!important}
        .hma-section-hd-title{font-family:'Syne',sans-serif;font-size:20px;font-weight:800;color:#fff;letter-spacing:-.01em}
        .hma-section-hd-sub{font-family:'Space Mono',monospace;font-size:10px;font-weight:700;color:rgba(255,255,255,0.44);letter-spacing:.08em;text-transform:uppercase}
        .hma-radar-card{background:#0c0c0c;border:1px solid rgba(201,168,76,0.11);border-radius:14px;padding:12px 16px;cursor:pointer;transition:transform .15s,border-color .15s,background .15s;text-align:left}
        .hma-radar-card:hover{background:#111;border-color:rgba(201,168,76,0.28);transform:translateY(-2px)}
        .hma-radar-scroll::-webkit-scrollbar{display:none}
        .hma-radar-scroll{-ms-overflow-style:none;scrollbar-width:none}
        .hma-feat-card{background:#0c0c0c;border:1px solid rgba(201,168,76,0.11);border-radius:16px;padding:24px 20px;transition:border-color .2s,transform .15s}
        .hma-feat-card:hover{border-color:rgba(201,168,76,0.28);transform:translateY(-3px)}
        .hma-earn-prev-card{background:#0c0c0c;border:1px solid rgba(201,168,76,0.1);border-radius:14px;padding:14px 16px;transition:border-color .15s}
        .hma-earn-prev-card:hover{border-color:rgba(201,168,76,0.25)}
        .hma-feat-strip{display:flex;background:rgba(201,168,76,0.02);border:1px solid rgba(201,168,76,0.1);border-radius:14px;overflow:hidden}
        .hma-feat-strip-item:hover{background:rgba(201,168,76,0.05)!important}
        @media(max-width:900px){
          .hma-home-two-col{grid-template-columns:1fr!important}
        }
        .hma-votd-hero{display:flex;gap:28px;align-items:flex-start}
        .hma-ticker-name{font-size:26px!important}
        .hma-company-name{font-size:14px!important;color:rgba(255,255,255,0.6)!important}
        .hma-stat-label{font-size:10px!important;color:rgba(255,255,255,0.72)!important;letter-spacing:.08em}
        .hma-stat-val{font-size:23px!important}
        .hma-section-title{font-size:15px!important;color:rgba(255,255,255,0.9)!important;font-weight:700!important}
        .hma-body-text{font-size:14px!important;color:rgba(255,255,255,0.75)!important;line-height:1.7!important}
        @media(max-width:680px){
          .hma-ticker-name{font-size:30px!important}
          .hma-bull-bear-col{padding:12px!important}
          .hma-bull-bear-col div[style*="font-size:12px"]{font-size:13px!important;line-height:1.65!important}
          .hma-company-name{font-size:15px!important;color:rgba(255,255,255,0.72)!important}
          .hma-stat-label{font-size:11px!important;color:rgba(255,255,255,0.65)!important}
          .hma-stat-val{font-size:24px!important}
          .hma-section-title{font-size:16px!important}
          .hma-body-text{font-size:15px!important;color:rgba(255,255,255,0.82)!important}
        }
        @media(max-width:480px){
          .hma-models-grid{grid-template-columns:1fr!important}
          .hma-exc-grid{grid-template-columns:1fr!important}
          .hma-hero-top{flex-direction:column!important;align-items:stretch!important}
          .hma-grade-dollar{width:100%!important;text-align:left!important}
          .hma-search-card{padding:16px!important}
          .hma-ticker-name{font-size:32px!important}
          .hma-stat-label{font-size:12px!important}
          .hma-hero-slogan{font-size:15px!important;overflow:hidden!important;text-overflow:ellipsis!important}
          .hma-votd-inner{padding:16px 14px!important}
          .hma-main-pad{padding:20px 14px 80px!important}
        }
      `}</style>

      {/* ══ HEADER ══ */}
      <PageHeader active="analyzer" />




      {/* ══ TICKER ══ */}
      <div className="hma-news-ticker" style={{ background:"#050505", overflow:"hidden", padding:"5px 0", borderBottom:"1px solid rgba(201,168,76,0.06)" }}>
        <div style={{ display:"flex", whiteSpace:"nowrap", animation:"hmaticker 60s linear infinite" }}>
          {(headlines.length>0 ? [...headlines,...headlines] : [
            {headline:"Loading today's market headlines…",source:"",sentiment:"neutral"},
            {headline:"12 valuation models · live FMP data · AI-powered analysis",source:"Korvus",sentiment:"neutral"},
          ]).map((s,i)=>{
            const dot = s.sentiment==="positive"?"#00C805":s.sentiment==="negative"?"#FF3B30":C.gold2;
            return (
              <span key={i} style={{ fontSize:12, color:"rgba(255,255,255,0.62)", padding:"0 28px", fontWeight:500, display:"inline-flex", alignItems:"center", gap:6 }}>
                <span style={{ width:5, height:5, borderRadius:"50%", background:dot, display:"inline-block", flexShrink:0 }}/>
                {s.headline}
                {s.source && <span style={{ fontSize:10, color:"rgba(255,255,255,0.25)", marginLeft:4 }}>— {s.source}</span>}
              </span>
            );
          })}
        </div>
      </div>

      {/* ══ MAIN ══ */}
      <div className="hma-main-pad" style={{ maxWidth:1200, margin:"0 auto", padding:"0 40px 60px" }}>
        <div>

          {/* ═ ANALYZER ═ */}
          <div>

            {/* ══ HERO (homepage only) ══ */}
            {!data && !loading && (
              <div className="hma-homepage-hero hma-fade-up" style={{ borderBottom:"1px solid rgba(201,168,76,0.08)", marginBottom:20 }}>
                <div className="hma-hero-slogan">
                  <span style={{ color:"#fff" }}>Run the numbers.&nbsp;</span>
                  <span style={{ color:"#e8c875" }}>Trust the math.</span>
                  <br className="hma-slogan-br"/>
                  <span className="hma-slogan-noise" style={{ color:"#fff" }}>&nbsp;Skip the noise.</span>
                </div>
                <div className="hma-search-row" style={{ marginBottom:12, position:"relative", zIndex:1 }}>
                  <input className="hma-input" type="text" placeholder="Ticker or company — AAPL, Apple, Coca-Cola…"
                    value={ticker} onChange={e=>setTicker(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&analyze()} maxLength={50}/>
                  <button className="hma-analyze-hero-btn" onClick={()=>analyze()}
                    style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:"13px 28px", fontSize:14, fontWeight:800, background:"linear-gradient(135deg,#8a5a10,#c9a84c,#e8c875)", color:"#000", border:"none", borderRadius:4, cursor:"pointer", whiteSpace:"nowrap", boxShadow:"0 0 24px rgba(201,168,76,0.4)", fontFamily:"'Syne',sans-serif", letterSpacing:".1em", textTransform:"uppercase" }}>
                    Analyze →
                  </button>
                </div>
                <div className="hma-chips-row" style={{ display:"flex", gap:6, alignItems:"center" }}>
                  <span style={{ fontSize:11, color:C.hint, flexShrink:0 }}>Try:</span>
                  {EXAMPLES.map(ex=><button key={ex} className="hma-chip" style={{ flexShrink:0 }} onClick={()=>analyze(ex)}>{ex}</button>)}
                </div>
              </div>
            )}

            {/* ══ COMPACT SEARCH (during loading or when showing results) ══ */}
            {(loading || data) && (
              <div style={{ marginBottom:16 }}>
                {recentTickers.length>0 && (
                  <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:10, flexWrap:"wrap" }}>
                    <span style={{ fontSize:11, color:"rgba(255,255,255,0.3)" }}>Recent:</span>
                    {recentTickers.map(t=>(
                      <button key={t} onClick={()=>analyze(t)} disabled={loading}
                        style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:12, fontWeight:700, padding:"3px 11px", borderRadius:20, background:"rgba(201,168,76,0.07)", border:"1px solid rgba(201,168,76,0.18)", color:C.gold2, cursor:"pointer", letterSpacing:"0.04em" }}>
                        {t}
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ display:"flex", gap:8 }}>
                  <input className="hma-input" type="text" placeholder="Ticker or company — AAPL, Apple, Coca-Cola…"
                    value={ticker} onChange={e=>setTicker(e.target.value)}
                    onKeyDown={e=>e.key==="Enter"&&!loading&&analyze()} maxLength={50}/>
                  <button className="hma-analyze-btn" onClick={()=>analyze()} disabled={loading}
                    style={{ padding:"13px 22px", fontSize:12, fontWeight:800, background:"linear-gradient(135deg,#8a5a10,#c9a84c,#e8c875)", color:"#000", border:"none", borderRadius:4, cursor:"pointer", whiteSpace:"nowrap", opacity:loading?0.7:1, boxShadow:"0 0 20px rgba(201,168,76,0.35)", fontFamily:"'Syne',sans-serif", letterSpacing:".1em", textTransform:"uppercase" }}>
                    {loading?"…":"Analyze →"}
                  </button>
                </div>
              </div>
            )}

            {/* VOTD — skeleton while fetching, hero card while idle (hidden during loading so game shows) */}
            {(votdLoading || (votd && !data && !loading)) && (
              votdLoading ? (
                <div style={{ background:"#0F0F0F", border:"1px solid rgba(201,168,76,0.15)", borderRadius:20, padding:"24px 28px", marginBottom:20 }}>
                  <div style={{ width:180, height:10, borderRadius:6, background:"rgba(201,168,76,0.12)", marginBottom:18, animation:"hmapulse2 1.4s ease-in-out infinite" }}/>
                  <div style={{ display:"flex", gap:24 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ width:200, height:52, borderRadius:8, background:"rgba(201,168,76,0.08)", marginBottom:12, animation:"hmapulse2 1.4s ease-in-out infinite" }}/>
                      <div style={{ width:"80%", height:16, borderRadius:6, background:"rgba(255,255,255,0.06)", marginBottom:8, animation:"hmapulse2 1.4s ease-in-out infinite" }}/>
                      <div style={{ width:"65%", height:13, borderRadius:6, background:"rgba(255,255,255,0.04)", animation:"hmapulse2 1.4s ease-in-out infinite" }}/>
                    </div>
                    <div style={{ width:170, height:140, borderRadius:14, background:"rgba(255,255,255,0.03)", animation:"hmapulse2 1.4s ease-in-out infinite" }}/>
                  </div>
                </div>
              ) : votd ? (
                <div style={{ background:"linear-gradient(145deg,#0D0800 0%,#180E00 60%,#0D0800 100%)", border:"1px solid rgba(201,168,76,0.35)", borderRadius:20, overflow:"hidden", marginBottom:20, boxShadow:"0 4px 48px rgba(201,168,76,0.1),0 0 0 1px rgba(201,168,76,0.05)" }}>
                  <div style={{ height:5, background:"linear-gradient(90deg,#5a3d0e,#e8c875,#ffe87a,#e8c875,#5a3d0e)", backgroundSize:"200% auto", animation:"hmaShimmer 4s linear infinite" }}/>
                  <div className="hma-votd-inner" style={{ padding:"22px 26px" }}>
                    <div className="hma-votd-card-hd" style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:20, flexWrap:"wrap", gap:8 }}>
                      <span style={{ fontSize:10, fontWeight:900, textTransform:"uppercase", letterSpacing:".2em", color:C.gold2, background:"rgba(201,168,76,0.1)", border:"1px solid rgba(201,168,76,0.25)", borderRadius:20, padding:"4px 16px" }}>◆ Valuation of the Day</span>
                      {votd.trendingSource==="stocktwits" && <span style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".12em", color:"rgba(255,255,255,0.45)", background:"rgba(255,255,255,0.05)", border:"1px solid rgba(255,255,255,0.1)", borderRadius:20, padding:"3px 10px", display:"inline-flex", alignItems:"center", gap:4 }}><Flame size={10} strokeWidth={2} color="#FF6B35"/>Trending on X</span>}
                      <span className="hma-votd-header-date" style={{ fontSize:11, color:C.hint }}>{new Date().toLocaleDateString("en-US",{month:"long",day:"numeric",year:"numeric"})}</span>
                    </div>
                    <div className="hma-votd-hero">
                      <div style={{ flex:1, minWidth:0 }}>
                        <div style={{ display:"flex", alignItems:"baseline", gap:14, marginBottom:10, flexWrap:"wrap" }}>
                          <span className="hma-votd-ticker" style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:58, fontWeight:900, lineHeight:1, color:"#ffe87a", letterSpacing:"-1px" }}>{votd.ticker}</span>
                          <span style={{ fontSize:14, color:"rgba(255,255,255,0.4)", fontWeight:400 }}>{votd.companyName}</span>
                        </div>
                        <div className="hma-votd-headline" style={{ fontSize:16, fontWeight:600, color:"rgba(255,255,255,0.9)", lineHeight:1.55, marginBottom:10 }}>{votd.headline}</div>
                        {votd.whyToday && <div className="hma-votd-why-today" style={{ fontSize:13, color:"rgba(255,255,255,0.48)", lineHeight:1.65, marginBottom:16 }}>{votd.whyToday}</div>}
                        <div className="hma-votd-catalyst-risk" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:10 }}>
                          {votd.catalyst && (
                            <div style={{ background:"rgba(0,200,5,0.07)", border:"1px solid rgba(0,200,5,0.2)", borderRadius:12, padding:"10px 14px" }}>
                              <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".12em", color:C.green, marginBottom:5, fontWeight:700 }}>▲ Catalyst</div>
                              <div style={{ fontSize:12, color:"rgba(255,255,255,0.7)", lineHeight:1.5 }}>{votd.catalyst}</div>
                            </div>
                          )}
                          {votd.risk && (
                            <div style={{ background:"rgba(255,59,48,0.07)", border:"1px solid rgba(255,59,48,0.2)", borderRadius:12, padding:"10px 14px" }}>
                              <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".12em", color:C.neg, marginBottom:5, fontWeight:700 }}>▼ Risk</div>
                              <div style={{ fontSize:12, color:"rgba(255,255,255,0.7)", lineHeight:1.5 }}>{votd.risk}</div>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="hma-votd-grade-col" style={{ minWidth:175, background:"rgba(0,0,0,0.45)", border:"1px solid rgba(201,168,76,0.2)", borderRadius:16, padding:"20px", textAlign:"center", flexShrink:0 }}>
                        <div className="hma-votd-grade-label" style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".18em", color:C.hint, marginBottom:12, fontWeight:600 }}>AI Grade</div>
                        <div className="hma-votd-grade-letter" style={{ fontFamily:"'Cinzel',serif", fontSize:58, fontWeight:900, lineHeight:1, color:GRADES[votd.grade]?.color || C.gold2, marginBottom:4 }}>{votd.grade || "?"}</div>
                        <div className="hma-votd-grade-dir" style={{ fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".1em", color:votd.direction==="undervalued"?C.green:C.neg, marginBottom:18 }}>{votd.direction || ""}</div>
                        <div className="hma-votd-grade-fv" style={{ borderTop:"1px solid rgba(201,168,76,0.15)", paddingTop:14, marginBottom:16 }}>
                          <div style={{ fontSize:11, textTransform:"uppercase", letterSpacing:".15em", color:C.hint, marginBottom:6, fontWeight:600 }}>Fair Value Range</div>
                          <div style={{ fontSize:17, fontWeight:800, color:C.text, marginBottom:3 }}>${votd.fairValueLow}–${votd.fairValueHigh}</div>
                          <div style={{ fontSize:11, color:C.hint }}>vs ${votd.currentPrice} today</div>
                        </div>
                        {loading ? (
                          <div style={{ fontSize:12, color:C.gold2, fontWeight:600, padding:"9px 0", animation:"hmapulse2 1.4s ease-in-out infinite", letterSpacing:".05em" }}>◆ Analyzing...</div>
                        ) : (
                          <button onClick={()=>analyze(votd.ticker)} className="hma-votd-grade-btn" style={{ width:"100%", padding:"9px 0", background:"linear-gradient(135deg,#e8c875,#ffe87a)", color:"#000", border:"none", borderRadius:10, fontSize:13, fontWeight:800, cursor:"pointer", letterSpacing:".05em" }}>Full Analysis →</button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ) : null
            )}

            {/* ══ HOTTEST STONKS + EARNINGS — side-by-side ══ */}
            {!data && !loading && (
              <div className="hma-home-two-col hma-fade-up" style={{ animationDelay:".1s" }}>

                {/* LEFT: Hottest on the Street — ranked fire list */}
                <div className="hma-two-col-cell">
                  <div style={{ marginBottom:14 }}>
                    <div className="hma-section-hd">
                      <div className="hma-section-hd-title">Hottest on the Street</div>
                      <div className="hma-section-hd-sub">Trending now — run our models before the crowd does</div>
                    </div>
                  </div>
                  <div className="hma-card-list" style={{ display:"flex", flexDirection:"column", gap:6 }}>
                    {radarTickers.filter(r=>!["SPY","QQQ","IWM","DIA","GLD","SLV","TLT","HYG","VTI","VOO","XLU","XLK","XLF","XLE","XLV","XLI","XLB","XLP","XLY","XLC","XBI","GDX","ARKK","ARKG","ARKW","SMH","SOXX","IBB","EEM","EFA","SQQQ","TQQQ","UVXY","VXX"].includes(r.symbol)).slice(0, 5).map((r,rankIdx)=>{
                      const mc = r.marketCap || 0;
                      const mcStr = mc >= 1e12 ? `$${(mc/1e12).toFixed(2)}T` : mc >= 1e9 ? `$${(mc/1e9).toFixed(1)}B` : mc >= 1e6 ? `$${(mc/1e6).toFixed(0)}M` : null;
                      const knownDesc={"AAPL":"Consumer tech giant with a trillion-dollar ecosystem of hardware, software, and services.","TSLA":"EV pioneer turned energy company. Volatile, controversial, and impossible to ignore.","NVDA":"The AI chip monopoly. Every data center on earth wants what they're selling.","META":"Owns Facebook, Instagram, and WhatsApp. Bet big on the metaverse. Quietly printing money.","AMZN":"Retail + cloud + ads. AWS alone is worth more than most companies on Earth.","MSFT":"Enterprise software + Azure cloud + OpenAI partnership. The boring stock that keeps winning.","GOOGL":"Search monopoly, YouTube, and a dark horse in the AI race. Ad revenue machine.","JPM":"America's biggest bank. When the economy sneezes, JPM feels it first.","BAC":"Consumer banking giant. Rate-sensitive and deeply tied to the US economy.","NFLX":"Streaming wars survivor. Finally making real money after years of burning cash.","COIN":"Crypto's public face. Trades like Bitcoin with extra volatility.","UBER":"Ride-share turned delivery giant. Burning less cash, finally profitable.","XOM":"Big Oil. Prints money when energy prices spike, a punching bag when they don't.","CVX":"Oil major with a cleaner balance sheet than most. Dividends that actually hold.","V":"Sits in the middle of every card swipe on earth. Nearly recession-proof.","MA":"Visa's twin. Different logo, same money printer.","VST":"Power generator riding the AI data center electricity demand wave.","CEG":"Nuclear energy operator. AI needs power. Nuclear is back in fashion.","ELF":"Budget beauty brand punching above its weight against L'Oréal and Estée Lauder.","XLU":"Utilities ETF. The boring-but-steady play when markets get scared.","XLK":"Tech sector ETF. Long Silicon Valley, short everything else.","WATT":"Wireless charging tech. Small cap with big ambitions and big risk."};
                      const hash = (r.symbol||"").split("").reduce((a,c)=>a+c.charCodeAt(0),0);
                      const cleanName = (r.name||r.symbol).replace(/ Inc\.?| Corp\.?| Ltd\.?| PLC\.?| ETF/i,"");
                      const fallbacks = ["Social media's latest obsession. Worth a closer look.","Street is buzzing on this one. Could be signal, could be noise.","Moving for a reason today. Dig in before the crowd does.","All eyes on this ticker right now. Earned the attention?","Trending hard. Fundamentals matter — check before you buy."];
                      const desc = knownDesc[r.symbol] || fallbacks[hash % fallbacks.length];
                      const rankColors = ["#e8c875","#C8841A","#A07020","#7A5518","#5A3E10"];
                      const rankEmojis = ["🔥","📈","⚡","👀","💡"];
                      return (
                        <button key={r.symbol} onClick={()=>analyze(r.symbol)} className="hma-stock-card"
                          style={{ display:"flex", gap:0, background:"linear-gradient(135deg,#0F0D09,#130E06)", border:"1px solid rgba(201,168,76,0.10)", borderRadius:13, padding:"0", cursor:"pointer", color:"inherit", textAlign:"left", transition:"all .18s", width:"100%", alignItems:"stretch", overflow:"hidden" }}
                          onMouseEnter={ev=>{ ev.currentTarget.style.background="linear-gradient(135deg,#1A1408,#201709)"; ev.currentTarget.style.borderColor="rgba(201,168,76,0.28)"; ev.currentTarget.style.transform="translateY(-1px)"; ev.currentTarget.style.boxShadow="0 6px 20px rgba(0,0,0,0.5)"; }}
                          onMouseLeave={ev=>{ ev.currentTarget.style.background="linear-gradient(135deg,#0F0D09,#130E06)"; ev.currentTarget.style.borderColor="rgba(201,168,76,0.10)"; ev.currentTarget.style.transform="none"; ev.currentTarget.style.boxShadow="none"; }}>
                          {/* Rank stripe */}
                          <div style={{ width:34, background:`linear-gradient(180deg,${rankColors[rankIdx]}20,${rankColors[rankIdx]}06)`, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", gap:1, flexShrink:0, borderRight:`1px solid ${rankColors[rankIdx]}16` }}>
                            <span style={{ fontSize:13 }}>{rankEmojis[rankIdx]}</span>
                            <span style={{ fontSize:11, fontWeight:900, color:rankColors[rankIdx]+"BB", fontFamily:"'Rajdhani',sans-serif", letterSpacing:".04em" }}>#{rankIdx+1}</span>
                          </div>
                          {/* Logo */}
                          <div style={{ display:"flex", alignItems:"center", padding:"10px 0 10px 12px", flexShrink:0 }}>
                            <TickerBadge symbol={r.symbol} size={42} glowColor={rankIdx===0?rankColors[0]:undefined}/>
                          </div>
                          {/* Text */}
                          <div style={{ flex:1, minWidth:0, padding:"10px 12px" }}>
                            <div className="hma-card-row" style={{ display:"flex", alignItems:"baseline", gap:6, marginBottom:2 }}>
                              <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:17, fontWeight:900, color:"#ffe87a", letterSpacing:".03em", lineHeight:1 }}>{r.symbol}</span>
                              {mcStr && <span className="hma-card-mc" style={{ fontSize:10, fontWeight:600, color:"rgba(201,168,76,0.4)", marginLeft:"auto", whiteSpace:"nowrap" }}>{mcStr}</span>}
                            </div>
                            <div style={{ fontSize:11, color:"rgba(255,255,255,0.38)", fontWeight:500, marginBottom:3 }}>{cleanName}</div>
                            <div className="hma-card-desc" style={{ fontSize:12, color:"rgba(255,255,255,0.72)", lineHeight:1.5 }}>{desc}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* RIGHT: Earnings This Week — calendar style */}
                <div className="hma-two-col-cell hma-earnings-col">
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:14, flexWrap:"wrap", gap:8 }}>
                    <div className="hma-section-hd hma-section-hd-blue">
                      <div className="hma-section-hd-title" style={{ display:"flex", alignItems:"center", gap:8 }}>
                        <Calendar size={16} strokeWidth={2} color="#60A5FA" style={{ flexShrink:0 }}/>
                        Reporting This Week
                      </div>
                      <div className="hma-section-hd-sub">Large-cap earnings — prep before the bell</div>
                    </div>
                    <a href="/earnings" style={{ fontSize:11, color:"#60A5FA", textDecoration:"none", fontWeight:700, whiteSpace:"nowrap", opacity:0.8 }}>Full calendar →</a>
                  </div>
                  {earningsPreview.length > 0 ? (
                    <div className="hma-card-list" style={{ display:"flex", flexDirection:"column", gap:6 }}>
                      {earningsPreview.slice(0, 5).map((e)=>{
                        const names={"AAPL":"Apple","TSLA":"Tesla","AMZN":"Amazon","GOOGL":"Alphabet","META":"Meta","MSFT":"Microsoft","NVDA":"NVIDIA","NFLX":"Netflix","JPM":"JPMorgan","BAC":"Bank of America","GS":"Goldman Sachs","XOM":"ExxonMobil","CVX":"Chevron","PFE":"Pfizer","JNJ":"Johnson & Johnson","COIN":"Coinbase","UBER":"Uber","ABNB":"Airbnb","V":"Visa","MA":"Mastercard","DIS":"Disney","UNH":"UnitedHealth","WMT":"Walmart","HD":"Home Depot"};
                        const name = names[e.symbol] || e.symbol;
                        const dt = new Date(e.date+"T12:00:00");
                        const dow = dt.toLocaleDateString("en-US",{weekday:"short"}).toUpperCase();
                        const day = dt.toLocaleDateString("en-US",{day:"numeric"});
                        const mon = dt.toLocaleDateString("en-US",{month:"short"}).toUpperCase();
                        const rev = e.revenueEstimated>0 ? (e.revenueEstimated>=1e9?`$${(e.revenueEstimated/1e9).toFixed(1)}B`:e.revenueEstimated>=1e6?`$${(e.revenueEstimated/1e6).toFixed(0)}M`:"") : "";
                        const epsLine = e.epsEstimated!=null ? `Est. $${e.epsEstimated.toFixed(2)} EPS${rev?` · ${rev} rev`:""}` : `Run the numbers before the call`;
                        return (
                          <button key={e.symbol+e.date} onClick={()=>analyze(e.symbol)} className="hma-stock-card hma-earnings-card"
                            style={{ display:"flex", gap:0, background:"linear-gradient(135deg,#060814,#080B18)", border:"1px solid rgba(96,165,250,0.12)", borderRadius:12, cursor:"pointer", color:"inherit", textAlign:"left", transition:"all .15s", width:"100%", alignItems:"stretch", overflow:"hidden" }}
                            onMouseEnter={ev=>{ ev.currentTarget.style.background="linear-gradient(135deg,#0A0F1E,#0C1222)"; ev.currentTarget.style.borderColor="rgba(96,165,250,0.35)"; ev.currentTarget.style.transform="translateY(-1px)"; }}
                            onMouseLeave={ev=>{ ev.currentTarget.style.background="linear-gradient(135deg,#060814,#080B18)"; ev.currentTarget.style.borderColor="rgba(96,165,250,0.12)"; ev.currentTarget.style.transform="none"; }}>
                            {/* Date badge */}
                            <div style={{ width:44, background:"linear-gradient(180deg,rgba(96,165,250,0.14),rgba(96,165,250,0.04))", borderRight:"1px solid rgba(96,165,250,0.10)", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", flexShrink:0, padding:"8px 0", gap:0 }}>
                              <span style={{ fontSize:11, fontWeight:900, color:"rgba(96,165,250,0.6)", letterSpacing:".06em" }}>{dow}</span>
                              <span style={{ fontSize:20, fontWeight:900, color:"#60A5FA", lineHeight:1.1, fontFamily:"'Rajdhani',sans-serif" }}>{day}</span>
                              <span style={{ fontSize:11, fontWeight:700, color:"rgba(96,165,250,0.4)", letterSpacing:".04em" }}>{mon}</span>
                            </div>
                            {/* Logo */}
                            <div style={{ display:"flex", alignItems:"center", padding:"10px 0 10px 12px", flexShrink:0 }}>
                              <TickerBadge symbol={e.symbol} size={40} glowColor="#60A5FA"/>
                            </div>
                            <div style={{ flex:1, minWidth:0, padding:"10px 12px" }}>
                              <div className="hma-earn-row" style={{ display:"flex", alignItems:"baseline", gap:6, marginBottom:2 }}>
                                <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:17, fontWeight:900, color:"#ffe87a", letterSpacing:".03em", lineHeight:1 }}>{e.symbol}</span>
                              </div>
                              <div style={{ fontSize:11, color:"rgba(255,255,255,0.35)", fontWeight:500, marginBottom:3 }}>{name !== e.symbol ? name : ""}</div>
                              <div className="hma-earn-narrative" style={{ fontSize:12, color:"rgba(96,165,250,0.75)", lineHeight:1.5, fontWeight:600 }}>{epsLine}</div>
                            </div>
                            <div style={{ display:"flex", alignItems:"center", paddingRight:10, color:"rgba(96,165,250,0.25)", fontSize:16 }}>›</div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ background:"linear-gradient(135deg,#060814,#080B18)", border:"1px solid rgba(96,165,250,0.1)", borderRadius:12, padding:"28px 16px", textAlign:"center" }}>
                      <Calendar size={24} strokeWidth={1.5} color="rgba(96,165,250,0.3)" style={{ marginBottom:8 }}/>
                      <div style={{ fontSize:13, color:"rgba(96,165,250,0.5)" }}>Loading earnings calendar…</div>
                    </div>
                  )}
                </div>

              </div>
            )}

            {/* ══ TOOLS STRIP ══ */}
            {!data && !loading && (
              <div className="hma-tools-section hma-fade-up" style={{ animationDelay:".2s", marginBottom:16 }}>
                <div className="hma-feat-strip">
                  {[
                    { href:"/rates",            title:"The Curve",          sub:"Yield curve, Fed & SOFR" },
                    { href:"/earnings",        title:"Earnings Calendar", sub:"Who's reporting this week" },
                    { href:"/mispriced-stocks",title:"Mispriced Stocks",  sub:"Weekly AI-curated list" },
                    { href:"/portfolio",       title:"Portfolio Analyzer", sub:"Risk-adjusted frontier" },
                  ].map((f,i)=>(
                    <a key={f.href} href={f.href} className="hma-feat-strip-item"
                      style={{ flex:1, display:"flex", alignItems:"center", gap:10, padding:"16px 18px", borderRight:"1px solid rgba(201,168,76,0.1)", textDecoration:"none", transition:"background .15s" }}
                      onMouseEnter={ev=>{ ev.currentTarget.style.background="rgba(201,168,76,0.05)"; }}
                      onMouseLeave={ev=>{ ev.currentTarget.style.background="transparent"; }}>
                      <span style={{ fontSize:10, flexShrink:0, color:"#e8c875" }}>◆</span>
                      <div>
                        <div style={{ fontFamily:"'Syne',sans-serif", fontSize:13, fontWeight:800, color:C.text, letterSpacing:".01em" }}>{f.title}</div>
                        <div style={{ fontFamily:"'Space Mono',monospace", fontSize:11, color:C.hint, marginTop:3, letterSpacing:".04em", textTransform:"uppercase" }}>{f.sub}</div>
                      </div>
                      {i === 3 && <span style={{ marginLeft:"auto", fontSize:11, color:C.gold2, fontWeight:700 }}>→</span>}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* Slim VOTD reminder when user views a different stock */}
            {votd && data && data.ticker !== votd.ticker && !loading && (
              <div style={{ background:"rgba(201,168,76,0.04)", border:"1px solid rgba(201,168,76,0.18)", borderRadius:12, padding:"10px 16px", marginBottom:12, display:"flex", alignItems:"center", justifyContent:"space-between", gap:12, flexWrap:"wrap" }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, flexWrap:"wrap" }}>
                  <span style={{ fontSize:11, fontWeight:800, textTransform:"uppercase", letterSpacing:"0.15em", color:C.gold2 }}>◆ Today's Pick</span>
                  <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:15, fontWeight:800, color:C.text }}>{votd.ticker}</span>
                  <span style={{ fontSize:11, color:C.hint }}>{votd.companyName}</span>
                </div>
                <button onClick={()=>analyze(votd.ticker)} style={{ fontSize:11, fontWeight:700, padding:"5px 14px", borderRadius:20, background:"linear-gradient(135deg,#e8c875,#ffe87a)", color:"#000", border:"none", cursor:"pointer", whiteSpace:"nowrap" }}>View Analysis →</button>
              </div>
            )}

            {loading && <LoadingGame stage={stage} onPoints={pts=>addPoints(pts,"game")}/>}

            {err === "__limit__" && (
              <div style={{ background:"rgba(201,168,76,0.07)", border:"1px solid rgba(201,168,76,0.3)", borderRadius:14, padding:"20px 20px", marginBottom:16, textAlign:"center" }}>
                <div style={{ marginBottom:8, display:"flex", justifyContent:"center" }}><BarChart2 size={28} strokeWidth={1.5} color="#e8c875"/></div>
                <div style={{ fontSize:15, fontWeight:800, color:C.gold2, marginBottom:6 }}>Free analyses used up for this month</div>
                <div style={{ fontSize:13, color:"rgba(255,255,255,0.82)", marginBottom:16 }}>You've run your 3 free analyses. Upgrade to Pro for unlimited access every month.</div>
                <a href="/subscribe" style={{ display:"inline-block", padding:"11px 28px", background:"linear-gradient(135deg,#e8c875,#ffe87a)", color:"#000", fontWeight:800, fontSize:13, borderRadius:10, textDecoration:"none" }}>Upgrade to Pro →</a>
              </div>
            )}
            {err && err !== "__limit__" && (
              <div style={{ background:"rgba(255,59,48,0.08)", border:"1px solid rgba(255,59,48,0.25)", borderRadius:12, padding:"13px 16px", color:"#FCA5A5", fontSize:13, marginBottom:16 }}>
                <span style={{ display:"inline-flex", alignItems:"center", gap:6 }}><AlertTriangle size={14} strokeWidth={2} color="#FCA5A5"/>{err}</span>
              </div>
            )}

            {data && !loading && (
              <div>
                {/* VOTD slim badge above analysis results */}
                {votd && data.ticker === votd.ticker && (
                  <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:10, marginBottom:14, padding:"8px 14px", background:"rgba(201,168,76,0.06)", border:"1px solid rgba(201,168,76,0.22)", borderRadius:10, flexWrap:"wrap" }}>
                    <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                      <span style={{ fontSize:11, fontWeight:900, textTransform:"uppercase", letterSpacing:".2em", color:C.gold2 }}>◆ Valuation of the Day</span>
                      <span style={{ fontSize:11, color:"rgba(255,255,255,0.82)" }}>{votd.whyToday || votd.headline}</span>
                    </div>
                    <span style={{ fontSize:11, color:C.hint, whiteSpace:"nowrap" }}>{new Date().toLocaleDateString("en-US",{month:"short",day:"numeric"})}</span>
                  </div>
                )}
                {/* ── Analyzer header — ticker + company above grade hero ── */}
                <div style={{ marginBottom:22 }}>
                  <div style={{ fontFamily:"'Space Mono',monospace", fontSize:10, color:C.goldMuted, letterSpacing:".18em", textTransform:"uppercase", marginBottom:10, display:"flex", alignItems:"center", gap:9 }}>
                    <span style={{ width:18, height:1, background:C.gold, opacity:.5, display:"inline-block" }}/>
                    Analyzer · {data.ticker} analyzed
                  </div>
                  <div className="hma-ah-ticker-row" style={{ display:"flex", alignItems:"baseline", gap:14, marginBottom:5, flexWrap:"wrap" }}>
                    <span className="hma-ticker-name" style={{ fontFamily:"'Syne',sans-serif", fontSize:42, fontWeight:800, color:C.gold3, letterSpacing:".04em", lineHeight:1 }}>{data.ticker}</span>
                    <span className="hma-company-name" style={{ fontFamily:"'Syne',sans-serif", fontSize:18, color:C.soft, fontWeight:500 }}>{data.companyName}</span>
                  </div>
                  <div style={{ fontFamily:"'Space Mono',monospace", fontSize:10.5, color:C.muted, letterSpacing:".07em", textTransform:"uppercase", lineHeight:1.9 }}>
                    {[tier?.label, fmtS(data.currentPrice)+" current", `${inc.length} models run`, data.dataDate].filter(Boolean).join(" · ")}
                    <br/><span style={{ color:"rgba(201,168,76,0.52)" }}>The ravens see what the market misses · NFA</span>
                  </div>
                  <div style={{ marginTop:8, display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
                    {data.overallConfidence && <ConfidenceBadge level={data.overallConfidence}/>}
                    {!shareOpen
                      ? <button onClick={()=>setShareOpen(true)} className="hma-share-btn-group" style={{ fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:20, background:"rgba(255,255,255,0.06)", color:C.hint, border:"1px solid rgba(255,255,255,0.1)", cursor:"pointer" }}>↗ Share</button>
                      : <div className="hma-share-btn-group" style={{ display:"flex", alignItems:"center", gap:5, flexWrap:"wrap" }}>
                          <span style={{ fontSize:10, color:C.hint }}>Share:</span>
                          {[
                            { label:"X", action:()=>{ const u=encodeURIComponent(`https://hail-mary-analytics.vercel.app/?t=${data.ticker}`); const t=encodeURIComponent(`$${data.ticker} is graded ${data.grade} by Korvus — fair value ${fmtS(data.compositeFairValue||data.fairValue)}, ${pct>=0?"+":""}${Number(pct).toFixed(1)}% ${pct>=0?"upside":"downside"}.`); window.open(`https://twitter.com/intent/tweet?text=${t}&url=${u}`,"_blank"); }},
                            { label:"Facebook", action:()=>{ const u=encodeURIComponent(`https://hail-mary-analytics.vercel.app/?t=${data.ticker}`); window.open(`https://www.facebook.com/sharer/sharer.php?u=${u}`,"_blank"); }},
                            { label:"Reddit", action:()=>{ const u=encodeURIComponent(`https://hail-mary-analytics.vercel.app/?t=${data.ticker}`); const t=encodeURIComponent(`$${data.ticker} graded ${data.grade} — ${pct>=0?"+":""}${Number(pct).toFixed(1)}% ${pct>=0?"upside":"downside"} | Korvus`); window.open(`https://www.reddit.com/submit?url=${u}&title=${t}`,"_blank"); }},
                            { label:copied?"✓ Copied":"Copy link", action:()=>{ navigator.clipboard.writeText(`https://hail-mary-analytics.vercel.app/?t=${data.ticker}`).catch(()=>{}); setCopied(true); setTimeout(()=>setCopied(false),2000); }},
                          ].map(opt=>(
                            <button key={opt.label} onClick={opt.action} style={{ fontSize:10, fontWeight:700, padding:"3px 10px", borderRadius:20, background:opt.label.includes("✓")?"rgba(0,200,5,0.12)":"rgba(255,255,255,0.06)", color:opt.label.includes("✓")?C.green:C.hint, border:`1px solid ${opt.label.includes("✓")?"rgba(0,200,5,0.25)":"rgba(255,255,255,0.1)"}`, cursor:"pointer" }}>{opt.label}</button>
                          ))}
                          <button onClick={()=>setShareOpen(false)} style={{ fontSize:10, padding:"3px 8px", borderRadius:20, background:"none", border:"none", color:C.hint, cursor:"pointer" }}>✕</button>
                        </div>
                    }
                  </div>
                </div>

                {/* ── Grade hero — 3-column grid matching demo ── */}
                <div className="hma-grade-hero-pad" style={{ background:g.bg, border:`1px solid ${g.border}`, borderRadius:18, padding:"24px 22px", marginBottom:16 }}>
                  <div className="hma-grade-hero-grid" style={{ display:"grid", gridTemplateColumns:"auto 1fr auto", gap:24, alignItems:"center" }}>

                    {/* LEFT: grade circle + label + scale bars */}
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:8 }}>
                      <GradeCircle grade={data.grade} score={score} size={80}/>
                      <div className="hma-grade-scale-bars" style={{ display:"flex", alignItems:"flex-end", gap:4, marginTop:10 }}>
                        {["A+","A","B+","B","C","D","F"].map(gr=>{
                          const gc=GRADES[gr]; const active=gr===data.grade;
                          return (
                            <div key={gr} style={{ textAlign:"center" }}>
                              <div style={{ width:26, height:active?13:5, background:active?gc.color:gc.bg, borderRadius:2, marginBottom:3 }}/>
                              <div style={{ fontFamily:"'Space Mono',monospace", fontSize:6, fontWeight:700, color:active?gc.color:gc.color+"44" }}>{gr}{active?"▲":""}</div>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* CENTER: stats in mobile-friendly order */}
                    <div className="hma-hero-center">
                      {/* 1. Current price — mobile-only (desktop shows it in price grid below) */}
                      {data.currentPrice && (
                        <div className="hma-mobile-only hma-hero-stat-grp">
                          <div className="hma-hero-lbl">Current Price</div>
                          <div style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:26, fontWeight:700, color:C.text, lineHeight:1 }}>{fmtS(data.currentPrice)}</div>
                        </div>
                      )}
                      {/* 2. Our target */}
                      <div className="hma-hero-stat-grp">
                        <div className="hma-hero-lbl">Our Target · {inc.length} Models</div>
                        <div className="hma-fv-big" style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:42, fontWeight:800, color:C.text, letterSpacing:"-.01em", lineHeight:1 }}>{fmtS(data.compositeFairValue||data.weightedFairValue||data.fairValue)}</div>
                        {(data.fairValueLow && data.fairValueHigh) && (
                          <div style={{ fontFamily:"'Inter',sans-serif", fontSize:11, color:C.soft, marginTop:3 }}>Range: {fmtS(data.fairValueLow)} — {fmtS(data.fairValueHigh)}</div>
                        )}
                      </div>
                      {/* 3. Street estimate */}
                      {data.analystTarget && (
                        <div className="hma-hero-stat-grp">
                          <div className="hma-hero-lbl">Street Estimate</div>
                          <div style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:28, fontWeight:800, color:"rgba(255,255,255,0.4)", lineHeight:1 }}>{fmtS(data.analystTarget)}</div>
                          {(data.analystTargetLow&&data.analystTargetHigh) && (
                            <div style={{ fontFamily:"'Inter',sans-serif", fontSize:11, color:C.soft, marginTop:2 }}>{fmtS(data.analystTargetLow)} – {fmtS(data.analystTargetHigh)}</div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* RIGHT: upside box + MoS */}
                    <div className="hma-gh-right" style={{ display:"flex", flexDirection:"column", gap:12, minWidth:190 }}>
                      <div className="hma-upside-box" style={{ background:upside?"rgba(0,200,5,0.07)":"rgba(255,59,48,0.07)", border:`1px solid ${upside?"rgba(0,200,5,0.24)":"rgba(255,59,48,0.24)"}`, borderRadius:14, padding:"14px 18px", textAlign:"center" }}>
                        <div className="hma-hero-lbl hma-mobile-only" style={{ marginBottom:6 }}>Delta vs Current</div>
                        <div className="hma-grade-dollar-val" style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:38, fontWeight:700, color:upside?C.green:C.neg, lineHeight:1 }}>{upside?"+":"-"}{fmt(Math.abs(dollar))}</div>
                        <div className="hma-grade-dollar-pct" style={{ fontSize:13, fontWeight:600, color:upside?C.green:C.neg, marginTop:4 }}>{sign(pct)}{Number(pct).toFixed(1)}% vs current</div>
                        <div className="hma-upside-label" style={{ fontFamily:"'Space Mono',monospace", fontSize:11, color:C.muted, letterSpacing:".1em", textTransform:"uppercase", marginTop:4 }}>Dollar {upside?"Upside":"Downside"}</div>
                      </div>
                      {(()=>{
                        const fv = data.compositeFairValue||data.weightedFairValue||data.fairValue;
                        const cp = data.currentPrice;
                        if (!fv||!cp) return null;
                        const mos = ((fv-cp)/fv)*100;
                        const barW = Math.min(Math.abs(mos),60)/60*100;
                        const col  = mos>0 ? C.green : C.neg;
                        return (
                          <div className="hma-mos-bar" style={{ display:"flex", alignItems:"center", gap:10 }}>
                            <span style={{ fontFamily:"'Space Mono',monospace", fontSize:11, color:C.muted, letterSpacing:".08em", textTransform:"uppercase", whiteSpace:"nowrap" }}>Margin of Safety</span>
                            <div style={{ flex:1, height:4, background:"rgba(255,255,255,0.06)", borderRadius:2, overflow:"hidden" }}>
                              <div style={{ height:"100%", width:`${barW}%`, background:col, borderRadius:2 }}/>
                            </div>
                            <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:16, fontWeight:700, color:col, whiteSpace:"nowrap" }}>{mos>=0?"+":""}{mos.toFixed(1)}%</span>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                {/* ── Mobile price range bar (mobile-only) ── */}
                {(()=>{
                  const bearV = data.fairValueLow;
                  const bullV = data.fairValueHigh;
                  const fairV = data.compositeFairValue||data.weightedFairValue||data.fairValue;
                  const cp    = data.currentPrice;
                  if (!bearV||!bullV||!cp) return null;
                  const range = bullV - bearV;
                  const markerPct = range > 0 ? Math.min(100, Math.max(0, ((cp - bearV) / range) * 100)) : 50;
                  return (
                    <div className="hma-mobile-price-range" style={{ display:"none", marginBottom:14, background:"rgba(255,255,255,0.03)", border:"1px solid rgba(201,168,76,0.12)", borderRadius:14, padding:"12px 14px" }}>
                      <div style={{ fontFamily:"'Space Mono',monospace", fontSize:11, color:"rgba(201,168,76,0.55)", letterSpacing:".14em", textTransform:"uppercase", marginBottom:10 }}>Price Range · Bear / Fair / Bull</div>
                      <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                        {/* Bear label */}
                        <div style={{ flexShrink:0, textAlign:"center" }}>
                          <div style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:13, fontWeight:800, color:"#FF3B30" }}>{fmtS(bearV)}</div>
                          <div style={{ fontFamily:"'Space Mono',monospace", fontSize:7.5, color:"rgba(255,59,48,0.6)", letterSpacing:".06em", textTransform:"uppercase", marginTop:1 }}>Bear</div>
                        </div>
                        {/* Bar with marker */}
                        <div style={{ flex:1, position:"relative", height:6, background:"rgba(255,255,255,0.07)", borderRadius:3 }}>
                          {/* Fair value tick */}
                          {fairV && range > 0 && (
                            <div style={{ position:"absolute", top:-3, left:`${Math.min(100,Math.max(0,((fairV-bearV)/range)*100))}%`, transform:"translateX(-50%)", width:2, height:12, background:"rgba(201,168,76,0.5)", borderRadius:1 }}/>
                          )}
                          {/* Current price marker */}
                          <div style={{ position:"absolute", top:"50%", left:`${markerPct}%`, transform:"translate(-50%,-50%)", width:14, height:14, borderRadius:"50%", background:"#fff", border:"2px solid rgba(201,168,76,0.9)", boxShadow:"0 0 6px rgba(201,168,76,0.5)", zIndex:2 }}/>
                        </div>
                        {/* Bull label */}
                        <div style={{ flexShrink:0, textAlign:"center" }}>
                          <div style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:13, fontWeight:800, color:"#00C805" }}>{fmtS(bullV)}</div>
                          <div style={{ fontFamily:"'Space Mono',monospace", fontSize:7.5, color:"rgba(0,200,5,0.6)", letterSpacing:".06em", textTransform:"uppercase", marginTop:1 }}>Bull</div>
                        </div>
                      </div>
                      {fairV && (
                        <div style={{ textAlign:"center", marginTop:6, fontFamily:"'Space Mono',monospace", fontSize:11, color:"rgba(201,168,76,0.6)", letterSpacing:".06em" }}>
                          Fair {fmtS(fairV)} · Current {fmtS(cp)}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* ── Price Comparison row ── */}
                <div className="hma-price-grid" style={{ display:"grid", gridTemplateColumns:data.analystTarget?"1fr 1fr 1fr":"1fr 1fr", gap:12, marginBottom:16 }}>
                  <div className="hma-price-box" style={{ background:C.card2, border:`1px solid ${C.bord}`, borderRadius:16, padding:"16px 18px" }}>
                    <div style={{ fontFamily:"'Space Mono',monospace", fontSize:11, color:C.muted, letterSpacing:".13em", textTransform:"uppercase", marginBottom:8 }}>Current Price · Live</div>
                    <div style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:34, fontWeight:800, color:C.text, letterSpacing:"-.01em", lineHeight:1 }}>{fmtS(data.currentPrice)}</div>
                  </div>
                  <div className="hma-price-box" style={{ background:C.card2, border:`1px solid ${C.bord}`, borderRadius:16, padding:"16px 18px" }}>
                    <div style={{ fontFamily:"'Space Mono',monospace", fontSize:11, color:C.muted, letterSpacing:".13em", textTransform:"uppercase", marginBottom:8 }}>Our Fair Value · {inc.length} Models</div>
                    <div style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:34, fontWeight:800, color:C.text, letterSpacing:"-.01em", lineHeight:1 }}>{fmtS(data.compositeFairValue||data.weightedFairValue||data.fairValue)}</div>
                    {(data.fairValueLow && data.fairValueHigh) && (
                      <div style={{ fontFamily:"'Inter',sans-serif", fontSize:12.5, color:C.soft, marginTop:3 }}>{fmtS(data.fairValueLow)} – {fmtS(data.fairValueHigh)} range</div>
                    )}
                    <div style={{ display:"inline-block", background:upside?"rgba(0,200,5,0.09)":"rgba(255,59,48,0.09)", border:`1px solid ${upside?"rgba(0,200,5,0.24)":"rgba(255,59,48,0.24)"}`, color:upside?C.green:C.neg, fontFamily:"'Syne',sans-serif", fontSize:10, fontWeight:700, padding:"3px 9px", borderRadius:6, marginTop:7, letterSpacing:".06em" }}>
                      {sign(pct)}{Number(pct).toFixed(1)}% {upside?"upside":"downside"} from current
                    </div>
                  </div>
                  {data.analystTarget && (
                    <div className="hma-price-box" style={{ background:C.card2, border:`1px solid ${C.bord}`, borderRadius:16, padding:"16px 18px" }}>
                      <div style={{ fontFamily:"'Space Mono',monospace", fontSize:11, color:C.muted, letterSpacing:".13em", textTransform:"uppercase", marginBottom:8 }}>Street Consensus · Analysts</div>
                      <div style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:34, fontWeight:800, color:C.text, letterSpacing:"-.01em", lineHeight:1 }}>{fmtS(data.analystTarget)}</div>
                      {(data.analystTargetLow||data.analystTargetHigh) && (
                        <div style={{ fontFamily:"'Inter',sans-serif", fontSize:12.5, color:C.soft, marginTop:3 }}>
                          {data.analystTargetLow?fmtS(data.analystTargetLow):""}{data.analystTargetLow&&data.analystTargetHigh?" – ":""}{data.analystTargetHigh?fmtS(data.analystTargetHigh):""}
                        </div>
                      )}
                      {(()=>{
                        const fv = data.compositeFairValue||data.weightedFairValue||data.fairValue;
                        if (!fv||!data.analystTarget) return null;
                        const gap = ((data.analystTarget-fv)/fv)*100;
                        return <div style={{ display:"inline-block", background:"rgba(201,168,76,0.09)", border:"1px solid rgba(201,168,76,0.24)", color:C.gold2, fontFamily:"'Syne',sans-serif", fontSize:10, fontWeight:700, padding:"3px 9px", borderRadius:6, marginTop:7, letterSpacing:".06em" }}>We're {gap<=0?"above":"below"} Wall St. by {Math.abs(gap).toFixed(1)}%</div>;
                      })()}
                    </div>
                  )}
                </div>

                <ConfidenceBanner level={data.overallConfidence} gaps={data.dataGaps}/>

                {/* Reverse DCF + Street vs Us */}
                {(()=>{
                  const beta    = data.fundamentals?.beta    || data.beta    || 1.0;
                  const eps     = data.fundamentals?.eps     || data.eps;
                  const cp      = data.currentPrice;
                  const ke      = 0.043 + (beta||1) * 0.055;
                  const impliedG = (eps && cp && eps > 0) ? (ke - eps/cp) : null;
                  const hasStreet = !!data.analystTarget;
                  const fv = data.compositeFairValue||data.weightedFairValue||data.fairValue;
                  if (!impliedG && !hasStreet) return null;
                  return (
                    <div className="hma-rdcf-grid" style={{ display:"grid", gridTemplateColumns:impliedG&&hasStreet?"1fr 1fr":"1fr", gap:10, marginBottom:16 }}>
                      {impliedG!=null && (
                        <div style={{ background:C.card, border:`1px solid ${C.bord}`, borderRadius:14, padding:"14px 16px" }}>
                          <div className="hma-stat-label" style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.15em", color:C.hint, marginBottom:6 }}>Reverse DCF · Implied Growth</div>
                          <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:6 }}>
                            <span style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:28, fontWeight:700, color:impliedG>=0?C.gold2:C.neg }}>{impliedG>=0?"+":""}{(impliedG*100).toFixed(1)}%</span>
                            <span style={{ fontSize:11, color:C.hint }}>per year, forever</span>
                          </div>
                          <div className="hma-body-text" style={{ fontSize:11, color:C.soft, lineHeight:1.6 }}>
                            At <span style={{ color:C.text, fontWeight:600 }}>{fmtS(cp)}</span>, the market is pricing in <span style={{ color:impliedG>=0?C.gold2:C.neg, fontWeight:700 }}>{(impliedG*100).toFixed(1)}% annual earnings growth</span> in perpetuity.
                            {impliedG < 0 && " The market may be pricing in earnings contraction or significant risk."}
                            {impliedG > 0.25 && " That's an aggressive bet — high-growth expectations baked in."}
                            {impliedG >= 0 && impliedG <= 0.08 && " Conservative expectations — modest growth priced in."}
                            {impliedG > 0.08 && impliedG <= 0.25 && " Moderate growth expectations embedded in the current price."}
                          </div>
                        </div>
                      )}
                      {hasStreet && fv && (
                        <div style={{ background:C.card, border:`1px solid ${C.bord}`, borderRadius:14, padding:"14px 16px" }}>
                          <div className="hma-stat-label" style={{ fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.15em", color:C.hint, marginBottom:6 }}>Street vs Us</div>
                          {[
                            { label:"Wall St. Consensus", val:data.analystTarget, ref:cp },
                            { label:"Our Fair Value",     val:fv,                 ref:cp },
                          ].map(row=>{
                            const diff = row.val - row.ref;
                            const pct  = (diff/row.ref)*100;
                            const pos  = diff >= 0;
                            return (
                              <div key={row.label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8 }}>
                                <div>
                                  <div style={{ fontSize:11, color:C.soft }}>{row.label}</div>
                                  <div style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:20, fontWeight:700, color:pos?C.green:C.neg }}>{fmtS(row.val)}</div>
                                </div>
                                <div style={{ textAlign:"right" }}>
                                  <div style={{ fontSize:11, color:C.hint }}>vs current</div>
                                  <div style={{ fontSize:14, fontWeight:700, color:pos?C.green:C.neg }}>{pos?"+":""}{pct.toFixed(1)}%</div>
                                </div>
                              </div>
                            );
                          })}
                          {(()=>{
                            const agree = (data.analystTarget>cp) === (fv>cp);
                            const gap   = Math.abs(((data.analystTarget-fv)/fv)*100);
                            return (
                              <div style={{ fontSize:11, color:C.hint, marginTop:4, paddingTop:8, borderTop:`1px solid ${C.bord}` }}>
                                {agree
                                  ? `Street & our models agree — both ${fv>cp?"see upside":"see downside"} from here. Gap between targets: ${gap.toFixed(1)}%.`
                                  : `Street & our models diverge. We ${fv>cp?"see upside":"see downside"}; Wall St. ${data.analystTarget>cp?"sees upside":"sees downside"}.`}
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* Investment Thesis — Bull/Bear + Catalysts unified */}
                {(data.bullCase?.length>0 || data.bearCase?.length>0 || data.catalysts?.length>0) && (
                  <div style={{ marginBottom:16 }}>
                    <div className="hma-section-label">Investment Thesis</div>
                  <div style={{ background:C.card2, border:`1px solid ${C.bord}`, borderRadius:18, overflow:"hidden" }}>
                    {(data.bullCase?.length>0 || data.bearCase?.length>0) && (
                      <div className="hma-bull-bear-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr", borderBottom:`1px solid ${C.bord}` }}>
                        <div className="hma-bull-bear-col" style={{ padding:"20px 22px", borderRight:`1px solid ${C.bord}` }}>
                          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:12, fontWeight:700, color:"#00C805", marginBottom:14, textTransform:"uppercase", letterSpacing:".14em", display:"flex", alignItems:"center", gap:7 }}><TrendingUp size={13} strokeWidth={2}/> Bull Case</div>
                          {(data.bullCase||[]).map((b,i)=>(
                            <div key={i} style={{ display:"flex", gap:10, marginBottom:12 }}>
                              <span style={{ width:20, height:20, borderRadius:"50%", background:"rgba(0,200,5,0.12)", border:"1px solid rgba(0,200,5,0.28)", color:"#00C805", fontSize:11, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:2, fontFamily:"'Syne',sans-serif" }}>{i+1}</span>
                              <div className="hma-bull-bear-text" style={{ fontFamily:"'Inter',sans-serif", fontSize:13.5, color:"rgba(255,255,255,0.80)", lineHeight:1.78 }}>{b.point||b}</div>
                            </div>
                          ))}
                        </div>
                        <div className="hma-bull-bear-col" style={{ padding:"20px 22px" }}>
                          <div style={{ fontFamily:"'Syne',sans-serif", fontSize:12, fontWeight:700, color:"#FF3B30", marginBottom:14, textTransform:"uppercase", letterSpacing:".14em", display:"flex", alignItems:"center", gap:7 }}><TrendingDown size={13} strokeWidth={2}/> Bear Case</div>
                          {(data.bearCase||[]).map((b,i)=>(
                            <div key={i} style={{ display:"flex", gap:10, marginBottom:12 }}>
                              <span style={{ width:20, height:20, borderRadius:"50%", background:"rgba(255,59,48,0.12)", border:"1px solid rgba(255,59,48,0.28)", color:"#FF3B30", fontSize:11, fontWeight:700, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:2, fontFamily:"'Syne',sans-serif" }}>{i+1}</span>
                              <div className="hma-bull-bear-text" style={{ fontFamily:"'Inter',sans-serif", fontSize:13.5, color:"rgba(255,255,255,0.80)", lineHeight:1.78 }}>{b.point||b}</div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {data.catalysts?.length>0 && (
                      <div>
                        <div style={{ padding:"10px 16px 6px", fontFamily:"'Space Mono',monospace", fontSize:11, fontWeight:700, textTransform:"uppercase", letterSpacing:".15em", color:C.hint }}>Key Catalysts · what moves the number</div>
                        <div className="hma-cat-grid" style={{ padding:"4px 12px 12px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                          {(data.catalysts||[]).map((c,i)=><CatalystCard key={i} catalyst={c}/>)}
                        </div>
                      </div>
                    )}
                  </div>
                  </div>
                )}

                {/* Peers */}
                {(data.peers||data.peerComparison)?.length>0 && <PeerAnalysis peers={(data.peers||data.peerComparison)} subjectTicker={data.ticker}/>}

                {/* Industry */}
                {(data.industryContext||data.industryBullets||data.industry)?.length>0 && <IndustryContext bullets={data.industryContext||data.industryBullets||data.industry}/>}

                {/* Fundamentals */}
                <div style={{ marginBottom:16 }}>
                  <div className="hma-section-label">Company Fundamentals</div>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                    {[
                      { label:"EPS (TTM)",  val:fmtS(data.fundamentals?.eps||data.eps) },
                      { label:"P/E Ratio",  val:fmtN(data.fundamentals?.peRatioTTM||data.peRatio), sub:`Ind. ${fmtN(data.fundamentals?.industryPE||data.industryPE)}` },
                      { label:"Beta",       val:fmtN(data.fundamentals?.beta||data.beta,2) },
                      { label:"Rev Growth", val:fmtP(data.fundamentals?.revenueGrowthRate||data.revenueGrowthRate) },
                      { label:"WACC",       val:fmtP(data.fundamentals?.wacc||data.wacc) },
                      { label:"Dividend",   val:fmtS(data.fundamentals?.dividendPerShare||data.dividendPerShare), sub:(data.paysDividends||data.dividends)?fmtP(data.fundamentals?.dividendGrowthRate||data.dividendGrowthRate)+" growth":"none" },
                    ].map(k=>(
                      <div key={k.label} className="hma-fundamentals-chip" style={{ background:C.card2, borderRadius:12, padding:"12px 16px", flex:"1 1 80px", minWidth:75, border:`1px solid ${C.bord}` }}>
                        <div style={{ fontFamily:"'Space Mono',monospace", fontSize:11, color:C.hint, textTransform:"uppercase", letterSpacing:"0.11em", marginBottom:5 }}>{k.label}</div>
                        <div style={{ fontFamily:"'Rajdhani',sans-serif", fontSize:22, fontWeight:800, color:C.text }}>{k.val}</div>
                        {k.sub && <div style={{ fontFamily:"'Inter',sans-serif", fontSize:11, color:C.soft, marginTop:2 }}>{k.sub}</div>}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Active models */}
                <div style={{ marginBottom:14 }}>
                  <div className="hma-section-label">{inc.length} Valuation Models</div>
                  <div className="hma-models-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
                    {inc.map(m=><ModelCard key={m.key||m.name} model={m} currentPrice={data.currentPrice}/>)}
                  </div>
                </div>

                {/* Excluded models */}
                {exc.length>0 && (
                  <div style={{ marginBottom:16 }}>
                    <div className="hma-section-label">Models Skipped</div>
                    <div style={{ fontSize:11, color:C.muted, marginBottom:10, lineHeight:1.5 }}>Not every model works for every company. These were benched — here's why in plain English.</div>
                    <div className="hma-exc-grid" style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
                      {exc.map(m=><ModelCard key={m.key||m.name} model={m} currentPrice={data.currentPrice}/>)}
                    </div>
                  </div>
                )}

                {/* Earnings warning */}
                {earningsDate && (
                  <div style={{ display:"flex", alignItems:"center", gap:10, background:"rgba(201,168,76,0.07)", border:"1px solid rgba(201,168,76,0.25)", borderRadius:12, padding:"11px 16px", marginBottom:14 }}>
                    <span style={{ fontSize:18 }}>📅</span>
                    <div>
                      <span style={{ fontSize:13, fontWeight:700, color:"#e8c875" }}>{data.ticker} reports earnings on {new Date(earningsDate).toLocaleDateString("en-US",{month:"long",day:"numeric"})}</span>
                      <span style={{ fontSize:12, color:"rgba(255,255,255,0.45)", marginLeft:8 }}>— this analysis may shift significantly after the report.</span>
                    </div>
                  </div>
                )}

                {/* Ask a follow-up */}
                <div style={{ background:C.card2, border:`1px solid ${C.bord}`, borderRadius:14, padding:"16px", marginBottom:14 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:C.soft, marginBottom:10 }}>💬 Ask a follow-up about this analysis</div>
                  <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                    <input
                      type="text"
                      value={followUpQ}
                      onChange={e=>setFollowUpQ(e.target.value)}
                      onKeyDown={e=>e.key==="Enter"&&!followUpLoading&&askFollowUp()}
                      placeholder={`Why is the DCF higher than the P/E for ${data.ticker}?`}
                      maxLength={200}
                      style={{ flex:"1 1 200px", padding:"10px 14px", fontSize:13, border:`1px solid ${C.bord}`, borderRadius:10, background:"#111", color:"#fff", outline:"none", fontFamily:"'Inter',sans-serif" }}
                    />
                    <button onClick={askFollowUp} disabled={followUpLoading||!followUpQ.trim()}
                      style={{ padding:"10px 18px", fontSize:13, fontWeight:700, background:"linear-gradient(135deg,#0D2E5A,#1B4F8A)", color:"#fff", border:"1px solid rgba(201,168,76,0.25)", borderRadius:10, cursor:"pointer", opacity:followUpLoading||!followUpQ.trim()?0.4:1, whiteSpace:"nowrap" }}>
                      {followUpLoading ? "…" : "Ask →"}
                    </button>
                  </div>
                  {followUpA && (
                    <div style={{ marginTop:12, padding:"12px 14px", background:"rgba(201,168,76,0.05)", border:"1px solid rgba(201,168,76,0.15)", borderRadius:10, fontSize:13, color:"rgba(255,255,255,0.85)", lineHeight:1.7 }}>
                      {followUpA}
                    </div>
                  )}
                </div>

                <div style={{ fontSize:11, color:C.hint, textAlign:"center", lineHeight:1.7, marginBottom:16, padding:"12px 16px", background:C.card2, borderRadius:12, border:`1px solid ${C.bord}` }}>
                  For educational purposes only. AI-sourced data may contain errors.<br/>
                  Always verify with primary sources before making investment decisions.
                </div>
                {/* Desktop action buttons */}
                <div className="hma-results-actions-desktop" style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
                  <button onClick={()=>{ setData(null); setTicker(""); setShareId(null); setWatchlistAdded(false); }}
                    style={{ flex:1, minWidth:160, padding:"13px", fontSize:13, fontWeight:600, background:"rgba(255,255,255,0.06)", color:C.soft, border:`1px solid ${C.bord}`, borderRadius:12, cursor:"pointer" }}>
                    ← Analyze another stock
                  </button>
                  {session && (
                    <button
                      disabled={watchlistAdded||watchlistLoading}
                      onClick={async()=>{
                        if (!session||watchlistAdded) return;
                        setWatchlistLoading(true);
                        try {
                          await fetch("/api/watchlist",{ method:"POST", headers:{"Content-Type":"application/json"},
                            body:JSON.stringify({ session, ticker:data.ticker, companyName:data.companyName, fairValue:data.compositeFairValue, fairValueLow:data.fairValueLow, fairValueHigh:data.fairValueHigh, currentPrice:data.currentPrice, grade:data.grade, direction:data.percentUpside>=0?"undervalued":"overvalued" })
                          });
                          setWatchlistAdded(true);
                        } catch {}
                        setWatchlistLoading(false);
                      }}
                      style={{ padding:"13px 18px", fontSize:13, fontWeight:600, background:watchlistAdded?"rgba(0,200,5,0.1)":"rgba(255,255,255,0.06)", color:watchlistAdded?C.green:C.soft, border:`1px solid ${watchlistAdded?"rgba(0,200,5,0.3)":C.bord}`, borderRadius:12, cursor:watchlistAdded?"default":"pointer", whiteSpace:"nowrap", transition:"all .2s" }}>
                      {watchlistLoading?"…":watchlistAdded?"✓ Watchlisted":"+ Watchlist"}
                    </button>
                  )}
                  <button onClick={async()=>{
                    if (shareId) {
                      const url = `${window.location.origin}/share/${shareId}`;
                      if (navigator.share) { navigator.share({ title:'Korvous Analysis', url }).catch(()=>{}); }
                      else { navigator.clipboard?.writeText(url).catch(()=>{}); }
                      return;
                    }
                    setShareLoading(true);
                    try {
                      const r = await fetch("/api/share",{ method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ ticker:data.ticker, analysisData:data }) });
                      const j = await r.json();
                      if (j.id) {
                        setShareId(j.id);
                        const url = `${window.location.origin}/share/${j.id}`;
                        if (navigator.share) { navigator.share({ title:'Korvous Analysis', url }).catch(()=>{}); }
                        else { navigator.clipboard?.writeText(url).catch(()=>{}); }
                      }
                    } catch {}
                    setShareLoading(false);
                  }}
                    style={{ padding:"13px 20px", fontSize:13, fontWeight:600, background:shareId?"rgba(0,200,5,0.08)":"rgba(201,168,76,0.08)", color:shareId?C.green:C.gold2, border:`1px solid ${shareId?"rgba(0,200,5,0.25)":"rgba(201,168,76,0.25)"}`, borderRadius:12, cursor:"pointer", whiteSpace:"nowrap" }}>
                    {shareLoading?"…":shareId?"✓ Link copied":"Share →"}
                  </button>
                </div>
                {/* Mobile sticky action footer — fixed above bottom nav, mobile-only */}
                <div className="hma-results-sticky-footer" style={{ display:"none" }}>
                  <button onClick={()=>{ setData(null); setTicker(""); setShareId(null); setWatchlistAdded(false); }}
                    style={{ flex:1, padding:"12px 10px", fontSize:12, fontWeight:700, background:"rgba(255,255,255,0.07)", color:C.soft, border:`1px solid ${C.bord}`, borderRadius:10, cursor:"pointer", whiteSpace:"nowrap" }}>
                    ← Another
                  </button>
                  {session && (
                    <button
                      disabled={watchlistAdded||watchlistLoading}
                      onClick={async()=>{
                        if (!session||watchlistAdded) return;
                        setWatchlistLoading(true);
                        try {
                          await fetch("/api/watchlist",{ method:"POST", headers:{"Content-Type":"application/json"},
                            body:JSON.stringify({ session, ticker:data.ticker, companyName:data.companyName, fairValue:data.compositeFairValue, fairValueLow:data.fairValueLow, fairValueHigh:data.fairValueHigh, currentPrice:data.currentPrice, grade:data.grade, direction:data.percentUpside>=0?"undervalued":"overvalued" })
                          });
                          setWatchlistAdded(true);
                        } catch {}
                        setWatchlistLoading(false);
                      }}
                      style={{ flex:1, padding:"12px 10px", fontSize:12, fontWeight:700, background:watchlistAdded?"rgba(0,200,5,0.12)":"rgba(255,255,255,0.07)", color:watchlistAdded?C.green:C.soft, border:`1px solid ${watchlistAdded?"rgba(0,200,5,0.3)":C.bord}`, borderRadius:10, cursor:watchlistAdded?"default":"pointer", whiteSpace:"nowrap", transition:"all .2s" }}>
                      {watchlistLoading?"…":watchlistAdded?"✓ Saved":"+ Watchlist"}
                    </button>
                  )}
                  <button onClick={async()=>{
                    if (shareId) {
                      const url = `${window.location.origin}/share/${shareId}`;
                      if (navigator.share) { navigator.share({ title:'Korvous Analysis', url }).catch(()=>{}); }
                      else { navigator.clipboard?.writeText(url).catch(()=>{}); }
                      return;
                    }
                    setShareLoading(true);
                    try {
                      const r = await fetch("/api/share",{ method:"POST", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ ticker:data.ticker, analysisData:data }) });
                      const j = await r.json();
                      if (j.id) {
                        setShareId(j.id);
                        const url = `${window.location.origin}/share/${j.id}`;
                        if (navigator.share) { navigator.share({ title:'Korvous Analysis', url }).catch(()=>{}); }
                        else { navigator.clipboard?.writeText(url).catch(()=>{}); }
                      }
                    } catch {}
                    setShareLoading(false);
                  }}
                    style={{ flex:1, padding:"12px 10px", fontSize:12, fontWeight:700, background:shareId?"rgba(0,200,5,0.1)":"rgba(201,168,76,0.1)", color:shareId?C.green:C.gold2, border:`1px solid ${shareId?"rgba(0,200,5,0.25)":"rgba(201,168,76,0.25)"}`, borderRadius:10, cursor:"pointer", whiteSpace:"nowrap" }}>
                    {shareLoading?"…":shareId?"✓ Copied":"Share →"}
                  </button>
                </div>
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
}
